/**
 * FDR Annual Assessments — vendor-level (operator order 2026-08-21 evening).
 *
 * The tab reads like the process: every vendor is a card; starting an
 * assessment assembles the audit tools from the vendor's delegated functions
 * (adjustable before starting); the engagement is one working grid — the
 * organization's real audit tools, verbatim — scored Met / Partially Met /
 * Not Met with evidence references and the carry-forward the Excel tools
 * always had. Closing computes the results page, feeds the vendor's risk,
 * and stamps the PHI destruction certificate.
 *
 * SEPARATE from the internal audit engine (never touched) and from the
 * legacy questionnaire (kept behind a link, read-only history).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import AttestationDashboard from './AttestationDashboard';

import SentEmailView from './SentEmailView';
import EmailReplyComposer from '../../components/EmailReplyComposer';
import AuditRecordPanel from '../Audit/AuditRecordPanel.jsx';
const API = process.env.REACT_APP_API_URL;

const jfetch = async (path, opts = {}) => {
  const r = await fetch(`${API}/api/fdr${path}`, { credentials: 'include', ...opts });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d?.message || 'Request failed');
  return d;
};

/**
 * Portal access panel (operator order 2026-08-25): who at the vendor can work
 * this audit. Additional participants must already be CONTACTS of the vendor;
 * they get contributor access — answer, upload, ask — optionally limited to
 * one audit tool. Only the vendor's compliance officer (from their portal)
 * and the compliance team here can grant or remove access. The primary
 * contact can never be removed.
 */
function ParticipantsPanel({ engagementId, sentAt }) {
  const [data, setData] = React.useState(null);
  const [contactId, setContactId] = React.useState('');
  const [toolScope, setToolScope] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [note, setNote] = React.useState('');
  const load = React.useCallback(() => {
    jfetch(`/engagements/${engagementId}/participants`).then(setData).catch(() => setData(null));
  }, [engagementId]);
  React.useEffect(() => { load(); }, [load]);
  if (!data) return null;
  const contacts = (data.contacts || []).filter((c) => !(data.participants || []).some((p) => p.contact_email && c.email && p.contact_email.toLowerCase() === c.email.toLowerCase()));
  return (
    <div className="mt-3 bg-white border border-slate-200 rounded-2xl p-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Portal access</div>
          <div className="text-[11px] text-slate-500 mt-0.5">Who at the vendor can work this audit. Colleagues must be on the vendor's Contacts tab; they answer and attach — the compliance officer signs and submits.</div>
        </div>
      </div>
      <ul className="mt-2 space-y-1">
        {(data.participants || []).map((p) => (
          <li key={p.id} className="flex items-center justify-between text-xs text-slate-700">
            <span>
              <b>{p.contact_name || p.contact_email}</b>
              <span className="text-slate-400"> · {p.contact_email} · {p.participant_role === 'primary' ? 'compliance officer' : (p.tool_scope || 'all audit tools')}{p.participant_role !== 'primary' && !p.last_used_at ? ' · invited, not yet joined' : ''}</span>
            </span>
            {p.participant_role !== 'primary' && (
              <button type="button" className="text-rose-600 hover:text-rose-700 text-xs"
                onClick={async () => {
                  try { await jfetch(`/engagements/${engagementId}/participants/${p.id}`, { method: 'DELETE' }); load(); }
                  catch (e) { setNote(e.message); }
                }}>Remove access</button>
            )}
          </li>
        ))}
        {(data.participants || []).length === 0 && <li className="text-xs text-slate-400">No portal access yet — it starts when the engagement letter is sent.</li>}
      </ul>
      {sentAt && (
        <div className="mt-3 flex items-end gap-2 flex-wrap">
          <div className="min-w-[14rem]">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Add a vendor contact</div>
            <select value={contactId} onChange={(e) => setContactId(e.target.value)} className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-xs bg-white">
              <option value="">Choose from the vendor's contacts…</option>
              {contacts.map((c) => <option key={c.id} value={c.id}>{[c.first_name, c.last_name].filter(Boolean).join(' ')}{c.title ? ` — ${c.title}` : ''}</option>)}
            </select>
          </div>
          <div className="min-w-[11rem]">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Their part</div>
            <select value={toolScope} onChange={(e) => setToolScope(e.target.value)} className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-xs bg-white">
              <option value="">Every audit tool</option>
              {(data.tools || []).map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <button type="button" disabled={busy || !contactId}
            className="px-3 py-1.5 rounded-lg bg-orange-500 text-white text-xs font-semibold disabled:opacity-50"
            onClick={async () => {
              setBusy(true); setNote('');
              try {
                await jfetch(`/engagements/${engagementId}/participants`, {
                  method: 'POST', headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ contact_id: contactId, tool_scope: toolScope || null }),
                });
                setContactId(''); setToolScope(''); setNote('Invite sent.');
                load();
              } catch (e) { setNote(e.message); }
              setBusy(false);
            }}>{busy ? 'Inviting…' : 'Grant access'}</button>
        </div>
      )}
      {note && <p className="mt-2 text-[11px] text-slate-600 font-medium">{note}</p>}
    </div>
  );
}

const FUNCTION_LABELS = {
  claims: 'Claims', um: 'Utilization Management', credentialing: 'Credentialing',
  network: 'Network Management', compliance_fwa: 'Compliance & FWA', custom: 'Custom',
};
const TYPE_LABELS = { annual: 'Annual', pre_delegation: 'Pre-delegation', for_cause: 'For-cause', follow_up: 'Follow-up' };
const VERDICTS = [
  { key: 'met', label: 'Met', cls: 'bg-emerald-50 text-emerald-700 ring-emerald-200' },
  { key: 'partially_met', label: 'Partial', cls: 'bg-amber-50 text-amber-700 ring-amber-200' },
  { key: 'not_met', label: 'Not met', cls: 'bg-rose-50 text-rose-700 ring-rose-200' },
  { key: 'na', label: 'N/A', cls: 'bg-slate-100 text-slate-500 ring-slate-200' },
];

function StagePill({ engagement }) {
  if (engagement.status === 'closed') {
    return <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700">Closed · {engagement.score != null ? `${Math.round(engagement.score)}%` : 'scored'}</span>;
  }
  if (engagement.submitted_at) return <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-sky-50 text-sky-700">Vendor submitted</span>;
  if (engagement.sent_at) return <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-indigo-50 text-indigo-700">With the vendor</span>;
  return <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-50 text-amber-700">In review</span>;
}

/** The process, readable at a glance — every engagement tells you where it is. */
function StageRail({ g }) {
  const steps = [
    { key: 'prepare', label: '1 · Prepare', done: true, active: !g.sent_at && g.status === 'open' },
    { key: 'notice', label: '2 · Notice sent', done: !!g.sent_at, active: false },
    { key: 'vendor', label: '3 · Vendor responds', done: !!g.submitted_at, active: !!g.sent_at && !g.submitted_at && g.status === 'open' },
    { key: 'review', label: '4 · Review & score', done: g.status === 'closed', active: !!g.submitted_at && g.status === 'open' },
    { key: 'closed', label: '5 · Closed', done: g.status === 'closed', active: false },
  ];
  return (
    <div className="flex items-center gap-1 flex-wrap">
      {steps.map((s, i) => (
        <React.Fragment key={s.key}>
          {i > 0 && <span className="text-slate-200">—</span>}
          <span className={`px-2 py-0.5 rounded-full text-[10.5px] font-semibold ${s.active ? 'bg-orange-500 text-white' : s.done ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-400'}`}>
            {s.done && !s.active ? '✓ ' : ''}{s.label}
          </span>
        </React.Fragment>
      ))}
    </div>
  );
}

/** Review the letter, confirm the address, send. Sending is always a human act. */
// Everyone the email should reach beyond the To: the auditor and the FDR's
// compliance officer ride automatically; the picker adds anyone else from
// the org. Rendered inside both send modals.
function CcPicker({ ccIds, setCcIds }) {
  const [team, setTeam] = useState([]);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  useEffect(() => { jfetch('/assessment-team').then((d) => setTeam(d.users || [])).catch(() => {}); }, []);
  const matches = team.filter((u) => !q.trim() || `${u.name || ''} ${u.email || ''}`.toLowerCase().includes(q.trim().toLowerCase()));
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Copied on the email</div>
      <div className="flex items-center gap-1.5 flex-wrap text-[11px] text-slate-600">
        <span className="px-1.5 py-px rounded bg-slate-100">You (the auditor)</span>
        <span className="px-1.5 py-px rounded bg-slate-100">The vendor&rsquo;s compliance officer</span>
        {team.filter((u) => ccIds.has(u.id)).map((u) => (
          <span key={u.id} className="px-1.5 py-px rounded bg-orange-50 text-orange-800">{u.name || u.email}
            <button onClick={() => setCcIds((p) => { const n = new Set(p); n.delete(u.id); return n; })} className="ml-1 text-orange-400 hover:text-orange-700">✕</button>
          </span>
        ))}
        <button onClick={() => { setOpen((v) => !v); setQ(''); }} className="font-semibold text-orange-700 hover:text-orange-900">{open ? 'Done' : '+ Copy someone else'}</button>
      </div>
      {open && (
        <div className="mt-1.5 border border-slate-200 rounded-lg bg-white">
          <input value={q} onChange={(e) => setQ(e.target.value)} autoFocus
            placeholder="Search by name or email — check everyone to copy"
            className="w-full text-xs border-b border-slate-100 rounded-t-lg px-2.5 py-1.5 focus:outline-none" />
          <div className="max-h-36 overflow-y-auto p-1.5 space-y-0.5">
            {matches.map((u) => (
              <label key={u.id} className="flex items-center gap-2 text-[11.5px] text-slate-600 hover:bg-orange-50 rounded px-1.5 py-1 cursor-pointer">
                <input type="checkbox" checked={ccIds.has(u.id)} className="accent-orange-600"
                  onChange={() => setCcIds((p) => { const n = new Set(p); if (n.has(u.id)) n.delete(u.id); else n.add(u.id); return n; })} />
                <span className="truncate">{u.name || u.email}</span>
                <span className="ml-auto text-slate-400 truncate">{u.email}</span>
              </label>
            ))}
            {!matches.length && <div className="text-[11px] text-slate-400 px-1.5 py-1">No one matches.</div>}
          </div>
        </div>
      )}
    </div>
  );
}

function SendModal({ engagement, onDone, onCancel }) {
  const [letter, setLetter] = useState('');
  const [toEmail, setToEmail] = useState('');
  const [contactName, setContactName] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [ccIds, setCcIds] = useState(() => new Set());
  // Additional vendor participants at send (operator order 2026-08-25):
  // contacts of the vendor who get their own contributor portal access.
  const [vendorContacts, setVendorContacts] = useState([]);
  const [extraIds, setExtraIds] = useState(() => new Set());
  useEffect(() => {
    jfetch(`/engagements/${engagement.id}/letter`).then((d) => {
      setLetter(d.letter_text || '');
      setToEmail((prev) => prev || d.suggested_email || engagement.compliance_officer_email || engagement.primary_contact_email || '');
      setContactName((prev) => prev || engagement.primary_contact_name || '');
    }).catch((e) => setErr(e.message));
    jfetch(`/engagements/${engagement.id}/participants`).then((d) => setVendorContacts(d.contacts || [])).catch(() => {});
  }, [engagement]);
  const send = async (manual = false) => {
    setBusy(true); setErr('');
    try {
      await jfetch(`/engagements/${engagement.id}/send`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to_email: toEmail, contact_name: contactName, letter_text: letter, manual, cc_user_ids: [...ccIds], additional_contact_ids: [...extraIds] }),
      });
      onDone();
    } catch (e) { setErr(e.message); setBusy(false); }
  };
  return createPortal(
    <div className="fixed inset-0 z-50 bg-slate-900/45 flex items-center justify-center p-4" onClick={onCancel}>
      <div className="bg-white rounded-2xl shadow-2xl w-[min(52rem,96vw)] max-h-[92vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-200">
          <div className="text-[15px] font-bold text-slate-900">Send the engagement letter</div>
          <div className="text-[11.5px] text-slate-500 mt-0.5">The vendor receives a secure portal link — they acknowledge, complete the tools, upload evidence, and e-sign. Review every word before sending; the sent letter is the record.</div>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4">
          {err && <div className="mb-2 text-[11.5px] font-semibold text-rose-600">{err}</div>}
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Vendor email</div>
              <input value={toEmail} onChange={(e) => setToEmail(e.target.value)} placeholder="compliance@vendor.com"
                className="w-full text-xs border border-slate-300 rounded-lg px-2.5 py-1.5" />
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Contact name</div>
              <input value={contactName} onChange={(e) => setContactName(e.target.value)} placeholder="e.g. Carla Walters"
                className="w-full text-xs border border-slate-300 rounded-lg px-2.5 py-1.5" />
            </div>
          </div>
          <div className="mb-3"><CcPicker ccIds={ccIds} setCcIds={setCcIds} /></div>
          {vendorContacts.filter((c) => c.email && c.email.toLowerCase() !== toEmail.trim().toLowerCase()).length > 0 && (
            <div className="mb-3">
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Also give portal access to</div>
              <div className="flex flex-wrap gap-2">
                {vendorContacts.filter((c) => c.email && c.email.toLowerCase() !== toEmail.trim().toLowerCase()).map((c) => {
                  const on = extraIds.has(c.id);
                  return (
                    <button key={c.id} type="button"
                      onClick={() => setExtraIds((prev) => { const n = new Set(prev); if (n.has(c.id)) n.delete(c.id); else n.add(c.id); return n; })}
                      className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border transition ${on ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-slate-600 border-slate-300 hover:border-slate-400'}`}>
                      {[c.first_name, c.last_name].filter(Boolean).join(' ')}{c.title ? ` · ${c.title}` : ''}
                    </button>
                  );
                })}
              </div>
              <p className="mt-1 text-[10.5px] text-slate-400">Each gets their own verified portal access to work the audit tools. Only the compliance officer above signs and submits. More can be added later, by you or by them.</p>
            </div>
          )}
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">The letter (edit freely)</div>
          <textarea value={letter} onChange={(e) => setLetter(e.target.value)} rows={18}
            className="w-full text-[11.5px] font-mono border border-slate-300 rounded-xl px-3 py-2.5 leading-relaxed" />
        </div>
        <div className="px-5 py-3 border-t border-slate-200 bg-slate-50/60 flex items-center gap-2">
          <button onClick={() => send(false)} disabled={busy || !toEmail.trim() || letter.trim().length < 200}
            className="px-4 py-2 rounded-xl bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold disabled:opacity-50">
            {busy ? 'Sending…' : 'Send to the vendor'}
          </button>
          <button onClick={() => send(true)} disabled={busy || !toEmail.trim() || letter.trim().length < 200}
            title="You sent (or will send) the letter from your own mailbox — this records it and moves the audit forward, no email from the platform"
            className="px-4 py-2 rounded-xl bg-white border border-slate-300 hover:border-slate-400 text-slate-600 text-xs font-semibold disabled:opacity-50">
            Mark as sent outside the platform
          </button>
          <button onClick={onCancel} className="px-4 py-2 rounded-xl bg-white border border-slate-300 text-slate-600 text-xs font-semibold">Cancel</button>
          <span className="ml-auto flex items-center gap-3">
            <button onClick={async () => {
              const { exportLetterDocx } = await import('./exportLetterDocx');
              const { fetchLogoDataUrl } = await import('./exportAsmtReportPdf');
              exportLetterDocx({ text: letter, fileBase: `Audit Notice - ${engagement.entity_name || 'vendor'} ${engagement.cycle_label}`, logoDataUrl: await fetchLogoDataUrl(`${API}/api/deck-studio/branding/logo`) });
            }} disabled={letter.trim().length < 200}
              className="text-[11px] font-semibold text-slate-500 hover:text-slate-700 disabled:opacity-40">Download Word</button>
            <button onClick={async () => {
              const { exportAsmtReportPdf, fetchLogoDataUrl } = await import('./exportAsmtReportPdf');
              exportAsmtReportPdf({ text: letter, fileBase: `Audit Notice - ${engagement.entity_name || 'vendor'} ${engagement.cycle_label}`, logoDataUrl: await fetchLogoDataUrl(`${API}/api/deck-studio/branding/logo`) });
            }} disabled={letter.trim().length < 200}
              className="text-[11px] font-semibold text-slate-500 hover:text-slate-700 disabled:opacity-40">Download PDF</button>
            <span className="text-[10.5px] text-slate-400">No PHI travels by email — substance lives behind the MFA portal.</span>
          </span>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ---------------------------------------------------------------------------
// Start assessment — tools prechecked from the vendor's delegated functions.
// ---------------------------------------------------------------------------
function StartModal({ vendor, tools, onDone, onCancel }) {
  const currentYear = new Date().getFullYear();
  const [form, setForm] = useState({
    cycle_label: `${currentYear - 1}-${currentYear}`,
    audit_type: 'annual', period_start: '', period_end: '', response_deadline: '',
  });
  const activeTools = tools.filter((t) => t.active);
  const [picked, setPicked] = useState(() => new Set(
    activeTools.filter((t) => vendor.function_keys.includes(t.function_key)).map((t) => t.id)));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const start = async () => {
    setBusy(true); setErr('');
    try {
      const d = await jfetch('/engagements', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entity_id: vendor.id, ...form, tool_ids: [...picked] }),
      });
      onDone(d.engagement);
    } catch (e) { setErr(e.message); setBusy(false); }
  };

  return createPortal(
    <div className="fixed inset-0 z-50 bg-slate-900/45 flex items-center justify-center p-4" onClick={onCancel}>
      <div className="bg-white rounded-2xl shadow-2xl w-[min(38rem,95vw)] max-h-[90vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-200">
          <div className="text-[15px] font-bold text-slate-900">Start assessment · {vendor.name}</div>
          <div className="text-[11.5px] text-slate-500 mt-0.5">The tools below were picked from what this vendor is delegated to do — adjust if needed.</div>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 space-y-4">
          {err && <div className="text-[11.5px] font-semibold text-rose-600">{err}</div>}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Audit cycle</div>
              <input value={form.cycle_label} onChange={(e) => setForm({ ...form, cycle_label: e.target.value })}
                className="w-full text-xs border border-slate-300 rounded-lg px-2.5 py-1.5" />
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Type</div>
              <select value={form.audit_type} onChange={(e) => setForm({ ...form, audit_type: e.target.value })}
                className="w-full text-xs border border-slate-300 rounded-lg px-2 py-1.5 bg-white">
                {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Audit period start</div>
              <input type="date" value={form.period_start} onChange={(e) => setForm({ ...form, period_start: e.target.value })}
                className="w-full text-xs border border-slate-300 rounded-lg px-2.5 py-1.5" />
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Audit period end</div>
              <input type="date" value={form.period_end} onChange={(e) => setForm({ ...form, period_end: e.target.value })}
                className="w-full text-xs border border-slate-300 rounded-lg px-2.5 py-1.5" />
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1" title="The vendor's due date for the first deliverable — completed tools, documents, and attestations. It is stamped into the engagement letter and shown on the board. The rebuttal deadline is set separately when the draft report goes out.">Vendor response due (first deliverable)</div>
              <input type="date" value={form.response_deadline} onChange={(e) => setForm({ ...form, response_deadline: e.target.value })}
                className="w-full text-xs border border-slate-300 rounded-lg px-2.5 py-1.5" />
            </div>
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">Audit tools for this vendor</div>
            {activeTools.length === 0 && <div className="text-[11.5px] text-slate-400">No tools in the library yet — import your audit tool workbooks under Manage tools.</div>}
            <div className="space-y-1.5">
              {activeTools.map((t) => (
                <label key={t.id} className="flex items-center gap-2.5 text-xs text-slate-700 cursor-pointer bg-slate-50 border border-slate-200 rounded-xl px-3 py-2">
                  <input type="checkbox" checked={picked.has(t.id)}
                    onChange={() => setPicked((prev) => { const n = new Set(prev); if (n.has(t.id)) n.delete(t.id); else n.add(t.id); return n; })}
                    className="accent-orange-600" />
                  <span className="font-semibold">{t.name}</span>
                  <span className="text-slate-400">{FUNCTION_LABELS[t.function_key] || t.function_key} · {t.element_count} elements</span>
                  {vendor.function_keys.includes(t.function_key) && <span className="ml-auto text-[10px] font-semibold text-emerald-600">delegated</span>}
                </label>
              ))}
            </div>
          </div>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 bg-slate-50/60 flex items-center gap-2">
          <button onClick={start} disabled={busy || !picked.size || !form.cycle_label.trim()}
            className="px-4 py-2 rounded-xl bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold disabled:opacity-50">
            {busy ? 'Assembling…' : 'Start assessment'}
          </button>
          <button onClick={onCancel} className="px-4 py-2 rounded-xl bg-white border border-slate-300 text-slate-600 text-xs font-semibold">Cancel</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ---------------------------------------------------------------------------
// The engagement — the working grid, verbatim from the org's audit tools.
// ---------------------------------------------------------------------------
function EngagementView({ engagementId, onBack, onChanged }) {
  const [data, setData] = useState(null);
  const [tool, setTool] = useState(null);
  const [section, setSection] = useState('overview'); // overview | fieldwork | correspondence | results
  const [uniOpen, setUniOpen] = useState(false); // universes strip inside Fieldwork
  const [filter, setFilter] = useState('all'); // all | open | flagged
  const [q, setQ] = useState('');
  const [expanded, setExpanded] = useState(() => new Set());
  const [closing, setClosing] = useState(false);
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [closeWarn, setCloseWarn] = useState(null);
  const [reopenOpen, setReopenOpen] = useState(false); // compliance officer only: reopen a closed audit
  const [reopenReason, setReopenReason] = useState('');
  const [reopenNotice, setReopenNotice] = useState(true);
  const [reopening, setReopening] = useState(false);
  const [findingFor, setFindingFor] = useState(null);
  const [sendOpen, setSendOpen] = useState(false);
  const [corrForm, setCorrForm] = useState(null); // { kind, subject, body }
  const [newMail, setNewMail] = useState(false); // the New email composer on Correspondence
  const [corrFiles, setCorrFiles] = useState([]);
  const [note, setNoteMsg] = useState('');
  const [err, setErr] = useState('');
  const saveTimers = useRef({});

  const load = useCallback(() => {
    jfetch(`/engagements/${engagementId}`).then((d) => {
      setData(d);
      setTool((t) => t || d.items[0]?.tool_name || null);
    }).catch((e) => setErr(e.message));
  }, [engagementId]);
  useEffect(() => { load(); }, [load]);

  const patchItem = (itemId, fields, immediate = false) => {
    setData((prev) => prev && ({ ...prev, items: prev.items.map((i) => (i.id === itemId ? { ...i, ...fields } : i)) }));
    const doSave = () => jfetch(`/engagements/items/${itemId}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(fields),
    }).catch((e) => setErr(e.message));
    if (immediate) return doSave();
    clearTimeout(saveTimers.current[itemId]);
    saveTimers.current[itemId] = setTimeout(doSave, 700);
  };

  if (!data) return <div className="p-8 text-xs text-slate-400">{err || 'Loading assessment…'}</div>;
  const g = data.engagement;
  const readOnly = g.status !== 'open';
  const tools = [...new Set(data.items.map((i) => i.tool_name))];
  const scored = data.items.filter((i) => i.verdict).length;
  const flaggedCount = data.items.filter((i) => i.verdict === 'not_met' || i.verdict === 'partially_met').length;

  const visible = data.items.filter((i) => {
    if (i.tool_name !== tool) return false;
    if (filter === 'open' && i.verdict) return false;
    if (filter === 'flagged' && i.verdict !== 'not_met' && i.verdict !== 'partially_met') return false;
    const needle = q.trim().toLowerCase();
    if (needle && !(`${i.label_path || ''} ${i.criteria_text} ${i.section}`).toLowerCase().includes(needle)) return false;
    return true;
  });

  const doClose = async (allowUnanswered) => {
    setClosing(true); setErr('');
    try {
      await jfetch(`/engagements/${g.id}/complete`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ allow_unanswered: allowUnanswered }),
      });
      setCloseWarn(null); load(); onChanged();
    } catch (e) {
      const m = e.message || '';
      const match = m.match(/(\d+) element/);
      if (match) setCloseWarn({ unanswered: Number(match[1]) });
      else setErr(m);
    }
    setClosing(false);
  };

  return (
    <div className="p-8 pt-4 max-w-7xl mx-auto w-full">
      <button onClick={onBack} className="text-[11.5px] font-semibold text-slate-500 hover:text-slate-700">← All vendors</button>
      <div className="mt-2 flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[17px] font-bold text-slate-900">{g.entity_name} · {TYPE_LABELS[g.audit_type]} assessment {g.cycle_label}</div>
          <div className="mt-0.5 text-[11.5px] text-slate-500">
            {g.period_start ? `Audit period ${g.period_start.slice(0, 10)} → ${g.period_end ? g.period_end.slice(0, 10) : '…'}` : 'No period set'}
            {g.response_deadline ? ` · response due ${g.response_deadline.slice(0, 10)}` : ''}
            {g.owner_name ? ` · ${g.owner_name}` : ''}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="text-[11px] text-slate-400">{scored} of {data.items.length} scored{flaggedCount ? ` · ${flaggedCount} flagged` : ''}</div>
            <div className="mt-1 w-44 h-1.5 rounded-full bg-slate-100 overflow-hidden">
              <div className="h-full bg-orange-500 rounded-full" style={{ width: `${data.items.length ? Math.round((scored / data.items.length) * 100) : 0}%` }} />
            </div>
          </div>
          <StagePill engagement={g} />
          {!readOnly && !g.sent_at && (
            <button onClick={() => setSendOpen(true)}
              title="Review the engagement notice, then send it through the portal or record it as sent from your own mailbox"
              className="px-3.5 py-2 rounded-xl bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold">
              Audit notice — review &amp; send
            </button>
          )}
          {/* Follow-ups are sent from Workstream, never from here — the FDR
              module is the record; Workstream is where the chasing happens
              (the same doctrine as the risk register). */}
          {!readOnly && g.sent_at && !g.submitted_at && (
            <span className="text-[10.5px] text-slate-400" title="Open Workstream, expand FDR Oversight, select this audit, and send the vendor a follow-up — the secure link rides the branded email and the send lands on this record.">
              Follow-ups go from Workstream
            </span>
          )}
          {!readOnly && (
            <button onClick={() => setPurgeOpen(true)} disabled={closing}
              title="Download the complete assessment record, then purge the working material and close with a destruction certificate"
              className="px-3.5 py-2 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold disabled:opacity-50">
              {closing ? 'Closing…' : 'Close & score'}
            </button>
          )}
          {g.status === 'closed' && data.can_reopen && (
            <button onClick={() => { setReopenReason(''); setReopenNotice(true); setReopenOpen(true); }} disabled={reopening}
              title="Compliance officer only: return this audit to open so the notice can go out again, participants can be re-invited and the work continues. The PHI destruction already certified is never undone."
              className="px-3.5 py-2 rounded-xl bg-white border border-slate-300 hover:border-orange-300 text-slate-800 text-xs font-semibold disabled:opacity-50">
              Reopen audit
            </button>
          )}
        </div>
      </div>
      {reopenOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/30 p-4" onClick={() => setReopenOpen(false)} role="dialog" aria-modal="true" aria-label="Reopen audit">
          <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-slate-200 p-5" onClick={(e) => e.stopPropagation()}>
            <div className="text-sm font-bold text-slate-900">Reopen this audit</div>
            <p className="mt-1 text-[12px] text-slate-600">The audit returns to open: the notice can be sent again, participants re-invited, universes requested again and the elements re-scored. The closure it had (score, dates, the PHI destruction certificate) stays on the record; the purged working material does not come back, the vendor supplies it again.</p>
            <label className="block mt-3 text-[11px] font-semibold text-slate-600">Reason (goes on the audit trail)</label>
            <textarea rows={3} className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-orange-200" value={reopenReason} onChange={(e) => setReopenReason(e.target.value)} placeholder="Why the audit is reopened, in a sentence" />
            <label className="mt-3 flex items-center gap-2 text-[12px] text-slate-700"><input type="checkbox" className="accent-orange-600" checked={reopenNotice} onChange={(e) => setReopenNotice(e.target.checked)} /> Let the audit notice be sent again (a fresh invitation to the vendor)</label>
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setReopenOpen(false)} className="px-3.5 py-2 rounded-xl bg-white border border-slate-200 text-slate-700 text-xs font-semibold">Cancel</button>
              <button disabled={reopening || reopenReason.trim().length < 5} onClick={async () => {
                setReopening(true); setErr('');
                try {
                  await jfetch(`/engagements/${g.id}/reopen`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ reason: reopenReason.trim(), allow_new_notice: reopenNotice }) });
                  setReopenOpen(false); setSection('overview'); load(); onChanged && onChanged();
                } catch (e) { setErr(e.message); }
                setReopening(false);
              }} className="px-3.5 py-2 rounded-xl bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold disabled:opacity-50">{reopening ? 'Reopening…' : 'Reopen'}</button>
            </div>
          </div>
        </div>
      )}
      <div className="mt-2.5"><StageRail g={g} /></div>
      <AuditRecordPanel g={g} jfetch={jfetch} onChanged={() => { load(); onChanged && onChanged(); }} readOnly={readOnly} purgeOpen={purgeOpen} setPurgeOpen={setPurgeOpen} />
      {(g.sent_at || note) && (
        <div className="mt-2 text-[11.5px] text-slate-500">
          {g.sent_at && <>Sent to <b className="text-slate-700">{g.sent_to}</b> on {new Date(g.sent_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</>}
          {g.ack_at && <> · receipt acknowledged by {g.ack_name}</>}
          {g.submitted_at && <> · <b className="text-sky-700">submitted {new Date(g.submitted_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</b></>}
          {g.vendor_note && <> · vendor note: “{g.vendor_note.slice(0, 140)}”</>}
          {note && <span className="text-emerald-700 font-semibold"> {note}</span>}
        </div>
      )}
      {err && <div className="mt-2 text-[11.5px] font-semibold text-rose-600">{err}</div>}

      {/* The workspace: one section at a time, never a mile of stacked cards. */}
      {(() => {
        // CMS phase language (operator order 2026-08-22): the audit tools and
        // the universes/samples ARE the field work — one tab, no split.
        const vendorFiles = (data.documents || []).filter((d) => d.category !== 'correspondence' && !d.correspondence_id).length;
        const SECTIONS = [
          ['overview', 'Overview', null],
          ['fieldwork', 'Fieldwork', `${scored}/${data.items.length}`],
          ['files', 'Files', vendorFiles || null],
          ['correspondence', 'Correspondence', (data.correspondence || []).length || null],
          ['results', 'Results', data.findings.length || null],
        ];
        return (
          <div className="mt-4 border-b border-slate-200 flex items-center gap-1 flex-wrap">
            {SECTIONS.map(([k, label, badge]) => (
              <button key={k} onClick={() => setSection(k)}
                className={`px-3.5 py-2 text-[12.5px] font-semibold border-b-2 -mb-px transition-colors ${section === k ? 'border-orange-500 text-slate-900' : 'border-transparent text-slate-400 hover:text-slate-600'}`}>
                {label}{badge != null && <span className="ml-1.5 px-1.5 py-px rounded-full bg-slate-100 text-[10px] text-slate-500 tabular-nums">{badge}</span>}
              </button>
            ))}
          </div>
        );
      })()}

      {section === 'overview' && (() => {
        const uniReceived = (data.universe_requests || []).filter((u) => u.status === 'received').length;
        const uniTotal = (data.universe_requests || []).length;
        const uniReturned = (data.universe_requests || []).filter((u) => u.status !== 'received' && u.feedback).length;
        const attSigned = (data.attestations || []).filter((a) => a.signed_at).length;
        const attTotal = (data.attestations || []).length;
        const sampleTotal = (data.sample_requests || []).length;
        const sampleIn = (data.sample_requests || []).filter((s) => s.status === 'received').length;
        const files = (data.documents || []).filter((d) => d.category !== 'correspondence' && !d.correspondence_id);
        const filesFromVendor = files.filter((d) => d.source === 'vendor');
        const latestFile = files.length ? files.map((d) => d.created_at).sort().at(-1) : null;
        const nextUp = g.status === 'closed' ? 'This assessment is closed — the results and the PHI destruction certificate are under Results.'
          : g.reopened_at && !g.sent_at ? 'This audit was reopened. Send the audit notice again (top right) to re-extend the invitation; the vendor supplies its material again, and the earlier closure stays on the record under Results.'
          : !g.sent_at ? 'Open "Audit notice — review & send" (top right). Review the notice, then either send it through the portal or record it as sent from your own mailbox — nothing goes out until you choose.'
          : !g.submitted_at ? 'The vendor is responding. Follow-ups go from Workstream (this audit is listed there); you can start scoring what has arrived.'
          : 'The vendor submitted. Score every element under Fieldwork, work the universes and samples, then Close & score.';
        const Card = ({ title, big, sub, tone = 'slate', onClick }) => (
          <button onClick={onClick} className={`text-left bg-white border border-slate-200 rounded-2xl p-4 hover:shadow-md hover:border-orange-200 transition-all`}>
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{title}</div>
            <div className={`mt-1 text-[22px] font-bold leading-none tabular-nums ${tone === 'rose' ? 'text-rose-600' : tone === 'emerald' ? 'text-emerald-600' : 'text-slate-900'}`}>{big}</div>
            <div className="mt-1 text-[11px] text-slate-500">{sub}</div>
          </button>
        );
        return (
          <div className="mt-4">
            <div className="bg-slate-50/70 border border-slate-200 rounded-2xl px-4 py-3 text-[12.5px] text-slate-700">
              <span className="font-semibold text-slate-500 mr-1.5">Next:</span>{nextUp}
            </div>
            <div className="mt-3 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              <Card title="Scoring" big={`${scored}/${data.items.length}`} sub={flaggedCount ? `${flaggedCount} flagged` : 'elements scored'} onClick={() => setSection('fieldwork')} />
              <Card title="Files" big={files.length || '—'}
                sub={files.length ? `${filesFromVendor.length} from the vendor · latest ${latestFile ? new Date(latestFile).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}` : 'nothing received yet'}
                tone={filesFromVendor.length ? 'emerald' : 'slate'} onClick={() => setSection('files')} />
              <Card title="Universes" big={uniTotal ? `${uniReceived}/${uniTotal}` : '—'}
                sub={uniReturned ? `${uniReturned} returned to vendor` : sampleTotal ? `${sampleIn}/${sampleTotal} samples in` : uniTotal ? 'received' : 'none requested'}
                tone={uniReturned ? 'rose' : 'slate'} onClick={() => { setSection('fieldwork'); setUniOpen(true); }} />
              <Card title="Attestations" big={attTotal ? `${attSigned}/${attTotal}` : '—'} sub={attTotal ? 'signed' : 'created at send'} tone={attTotal && attSigned === attTotal ? 'emerald' : 'slate'} onClick={() => setSection('results')} />
              <Card title="Findings" big={data.findings.length} sub="into Findings & CAPs" tone={data.findings.length ? 'rose' : 'slate'} onClick={() => setSection('results')} />
              <Card title={g.status === 'closed' ? 'Score' : 'Status'} big={g.status === 'closed' ? (g.score != null ? `${Math.round(g.score)}%` : '—') : (g.submitted_at ? 'Review' : g.sent_at ? 'Vendor' : 'Prepare')}
                sub={g.status === 'closed' ? (g.phi_purged_at ? 'PHI purge certified' : 'closed') : (g.response_deadline ? `due ${g.response_deadline.slice(0, 10)}` : 'no deadline set')}
                tone={g.status === 'closed' ? 'emerald' : 'slate'} onClick={() => setSection('results')} />
            </div>
            {g.status !== 'closed' && <ParticipantsPanel engagementId={g.id} sentAt={g.sent_at} />}
          </div>
        );
      })()}

      {section === 'files' && (
        <VendorFilesPanel data={data} />
      )}

      {/* FIELDWORK — the testing IS the tab. The data requests (universes and
          sample case files, CMS Phase I/II language) live in one slim band
          above the grid: a quiet summary line, the full workbench one click
          away, lit up only when something needs the auditor's attention. */}
      {section === 'fieldwork' && (() => {
        const uni = data.universe_requests || [];
        const samples = data.sample_requests || [];
        if (!uni.length && readOnly) return null;
        const received = uni.filter((u) => u.status === 'received').length;
        const returned = uni.filter((u) => u.status !== 'received' && u.feedback).length;
        const samplesIn = samples.filter((s) => s.status === 'received').length;
        return (
          <div className={`mt-4 border rounded-2xl px-4 py-2.5 flex items-center gap-3 flex-wrap ${returned ? 'bg-rose-50/50 border-rose-200' : 'bg-white border-slate-200'}`}>
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Universes &amp; samples</span>
            {uni.length > 0 ? (<>
              <span className="text-[11.5px] text-slate-600">{received}/{uni.length} universes in</span>
              {samples.length > 0 && <span className="text-[11.5px] text-slate-600">· {samplesIn}/{samples.length} sample sets in</span>}
              {returned > 0 && <span className="px-1.5 py-px rounded-full bg-rose-100 text-[10.5px] font-semibold text-rose-700">{returned} returned for correction</span>}
            </>) : (
              <span className="text-[11.5px] text-slate-400">None requested — request them here when the audit needs data</span>
            )}
            <button onClick={() => setUniOpen((v) => !v)}
              className="ml-auto text-[11.5px] font-semibold text-orange-700 hover:text-orange-900">
              {uniOpen ? 'Hide' : uni.length ? 'Open' : '+ Request'}
            </button>
          </div>
        );
      })()}
      {section === 'fieldwork' && uniOpen && (
        <UniversePanel data={data} g={g} readOnly={readOnly} onChanged={load} setErr={setErr} />
      )}
      {section === 'fieldwork' && (<>
      <div className="mt-4 flex items-center gap-2 flex-wrap">
        {tools.map((t) => (
          <button key={t} onClick={() => setTool(t)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${tool === t ? 'bg-orange-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
            {t}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-2">
          {[['all', 'All'], ['open', 'Needs verdict'], ['flagged', 'Flagged']].map(([k, label]) => (
            <button key={k} onClick={() => setFilter(k)}
              className={`px-2.5 py-1 rounded-lg text-[11.5px] font-semibold ${filter === k ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
              {label}
            </button>
          ))}
          <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search requirements…"
            className="w-56 text-xs border border-slate-200 rounded-lg px-2.5 py-1.5 bg-white focus:outline-none focus:border-orange-400" />
        </div>
      </div>

      <div className="mt-3 bg-white border border-slate-200 rounded-2xl overflow-hidden">
        {visible.length === 0 && <div className="py-10 text-center text-xs text-slate-400">Nothing matches.</div>}
        {(() => {
          let lastSection = null;
          return visible.map((item) => {
            const showSection = item.section !== lastSection;
            lastSection = item.section;
            const isOpen = expanded.has(item.id);
            return (
              <React.Fragment key={item.id}>
                {showSection && (
                  <div className="px-4 pt-3.5 pb-1 text-[10px] font-bold uppercase tracking-wider text-orange-700 bg-slate-50/50 border-t border-slate-100 first:border-t-0">
                    {item.section}
                  </div>
                )}
                <div className={`px-4 py-2.5 border-t border-slate-50 hover:bg-slate-50/40 ${item.verdict === 'not_met' ? 'bg-rose-50/30' : ''}`}>
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <span className="inline-flex items-center gap-1 mr-1.5">
                        {item.label_path && <span className="inline-block mb-0.5 px-1.5 py-px rounded bg-slate-100 text-[10px] font-semibold text-slate-500">{item.label_path}</span>}
                        <button
                          title={g.submitted_at ? 'What the vendor was asked for' : 'What the vendor must provide — click to change (Text → File → Both)'}
                          disabled={readOnly || !!g.submitted_at}
                          onClick={(e) => {
                            e.stopPropagation();
                            const next = item.response_mode === 'text' ? 'attachment' : item.response_mode === 'attachment' ? 'both' : 'text';
                            patchItem(item.id, { response_mode: next }, true);
                          }}
                          className={`mb-0.5 px-1.5 py-px rounded text-[10px] font-semibold ${
                            item.response_mode === 'attachment' ? 'bg-sky-50 text-sky-700'
                            : item.response_mode === 'both' ? 'bg-violet-50 text-violet-700' : 'bg-slate-50 text-slate-400'} ${!readOnly && !g.submitted_at ? 'hover:ring-1 hover:ring-orange-300' : ''}`}>
                          {item.response_mode === 'attachment' ? 'File' : item.response_mode === 'both' ? 'Text + File' : 'Text'}
                        </button>
                      </span>
                      <div className="text-[12.5px] text-slate-800 leading-snug">{item.criteria_text}</div>
                      {item.guidance && (
                        <div className="mt-0.5 text-[11px] text-slate-500 leading-snug whitespace-pre-wrap">{item.guidance}</div>
                      )}
                      {item.vendor_response && (
                        <div className="mt-1.5 bg-sky-50/70 border-l-2 border-sky-300 rounded-r-lg px-2.5 py-1.5 text-[11.5px] text-slate-700">
                          <span className="text-[9.5px] font-bold uppercase tracking-wider text-sky-600 mr-1.5">Vendor</span>
                          {item.vendor_response}
                          {item.doc_status && <span className="ml-1.5 text-[10px] text-sky-600">({item.doc_status === 'previous_effective' ? 'previous document remains effective' : 'new/updated document submitted'})</span>}
                        </div>
                      )}
                      {(data.documents || []).filter((d) => d.item_id === item.id).map((d) => (
                        <div key={d.id} className="mt-1 inline-flex items-center gap-1.5 mr-2 text-[11px] text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-2 py-1">
                          <span>📎</span><span className="truncate max-w-[16rem]">{d.file_name}</span>
                          {d.purged
                            ? <span className="text-[10px] font-semibold text-slate-400">Purged</span>
                            : <a href={`${API}/api/fdr/documents/${d.id}/download`} onClick={(e) => e.stopPropagation()} className="text-[10.5px] font-semibold text-orange-700 hover:text-orange-900 no-underline">Open</a>}
                        </div>
                      ))}
                    </div>
                    <div className="flex items-center gap-1 flex-none pt-0.5">
                      {VERDICTS.map((v) => (
                        <button key={v.key} disabled={readOnly}
                          onClick={() => patchItem(item.id, { verdict: item.verdict === v.key ? null : v.key }, true)}
                          className={`px-2 py-1 rounded-lg text-[11px] font-semibold ring-1 transition-colors ${item.verdict === v.key ? v.cls : 'bg-white text-slate-400 ring-slate-200 hover:text-slate-600'} disabled:cursor-default`}>
                          {v.label}
                        </button>
                      ))}
                      <button onClick={() => setExpanded((prev) => { const n = new Set(prev); if (n.has(item.id)) n.delete(item.id); else n.add(item.id); return n; })}
                        className="ml-1 text-slate-300 hover:text-slate-500 text-xs" title="Evidence and comments">{isOpen ? '▴' : '▾'}</button>
                    </div>
                  </div>
                  {(isOpen || item.evidence_ref || item.auditor_comments) && (
                    <div className="mt-2 grid md:grid-cols-2 gap-2.5 pl-0.5">
                      <div>
                        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">Supporting documents (name them)</div>
                        <input defaultValue={item.evidence_ref || ''} disabled={readOnly}
                          onChange={(e) => patchItem(item.id, { evidence_ref: e.target.value })}
                          placeholder="e.g. Policy CL-101 v4, training roster 2026"
                          className="w-full text-xs border border-slate-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:border-orange-400 disabled:bg-slate-50" />
                        <select value={item.doc_status || ''} disabled={readOnly}
                          onChange={(e) => patchItem(item.id, { doc_status: e.target.value || null }, true)}
                          className="mt-1.5 text-[11px] border border-slate-200 rounded-lg px-2 py-1 bg-white text-slate-600 disabled:bg-slate-50">
                          <option value="">Document status…</option>
                          <option value="new_updated">New / updated document submitted</option>
                          <option value="previous_effective">Previous document remains effective</option>
                        </select>
                      </div>
                      <div>
                        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">Vendor response — recorded by staff when received outside the portal</div>
                        <textarea defaultValue={item.vendor_response || ''} disabled={readOnly || !!g.submitted_at} rows={2}
                          placeholder="Transcribe the vendor's answer as received (email, call, paper)"
                          onChange={(e) => patchItem(item.id, { vendor_response: e.target.value })}
                          className="w-full text-xs border border-slate-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:border-orange-400 disabled:bg-slate-50" />
                        {!readOnly && (
                          <label className="inline-block mt-1 text-[11px] font-semibold text-slate-500 hover:text-slate-700 cursor-pointer"
                            title="The vendor returned the completed questionnaire or evidence by email — attach the file to this element">
                            Attach the vendor&rsquo;s file
                            <input type="file" className="hidden" onChange={async (e) => {
                              const f = e.target.files?.[0]; e.target.value = '';
                              if (!f) return;
                              try {
                                const fd = new FormData(); fd.append('file', f);
                                await jfetch(`/engagements/items/${item.id}/upload`, { method: 'POST', body: fd });
                                load();
                              } catch (ex) { setErr(ex.message); }
                            }} />
                          </label>
                        )}
                        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-0.5 mt-2">Auditor comments</div>
                        <textarea defaultValue={item.auditor_comments || ''} disabled={readOnly} rows={2}
                          onChange={(e) => patchItem(item.id, { auditor_comments: e.target.value })}
                          className="w-full text-xs border border-slate-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:border-orange-400 disabled:bg-slate-50" />
                        {!readOnly && (item.verdict === 'not_met' || item.verdict === 'partially_met') && (
                          <button onClick={() => setFindingFor(item)}
                            className="mt-1 text-[11px] font-semibold text-rose-600 hover:text-rose-800">Log finding →</button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </React.Fragment>
            );
          });
        })()}
      </div>
      </>)}

      {section === 'results' && (<>
      {g.status === 'closed' && (
        <div className="mt-4 bg-emerald-50/60 border border-emerald-200 rounded-2xl px-4 py-3 text-[12px] text-emerald-900 flex flex-wrap gap-x-6 gap-y-1">
          <span><b>{g.score != null ? `${Math.round(g.score)}%` : '—'}</b> score</span>
          <span><b>{g.met_count}</b> met</span>
          <span><b>{g.partial_count}</b> partially met</span>
          <span><b>{g.not_met_count}</b> not met</span>
          <span><b>{g.na_count}</b> N/A</span>
          {g.phi_purged_at && <span className="text-emerald-700">PHI purge certified {new Date(g.phi_purged_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>}
        </div>
      )}
      {g.status === 'closed' && g.phi_purge_certificate && (
        <div className="mt-3 bg-white border border-slate-200 rounded-2xl px-4 py-3">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">PHI destruction certificate</div>
          <div className="text-[12px] text-slate-700">{g.phi_purge_certificate.phi_working_material}</div>
          {(g.phi_purge_certificate.purged_artifacts || []).length > 0 && (
            <div className="mt-2 space-y-1">
              {g.phi_purge_certificate.purged_artifacts.map((a, i) => (
                <div key={i} className="flex items-center gap-2 text-[11.5px] text-slate-600 bg-slate-50 rounded-lg px-2.5 py-1.5">
                  <span className="truncate flex-1">{a.file_name}</span>
                  <span className="tabular-nums text-slate-400">{Math.round((a.bytes || 0) / 1024)} KB erased</span>
                  <span className="font-mono text-[10px] text-slate-400" title={`sha256 ${a.sha256}`}>{(a.sha256 || '').slice(0, 16)}…</span>
                </div>
              ))}
            </div>
          )}
          <div className="mt-1.5 text-[10.5px] text-slate-400">
            Erasure verified — zero universe or sample bytes remain. Metadata and hashes are retained as the record.
          </div>
        </div>
      )}
      {Array.isArray(g.reopen_history) && g.reopen_history.length > 0 && (
        <div className="mt-3 bg-amber-50/60 border border-amber-200 rounded-2xl px-4 py-3">
          <div className="text-[10px] font-bold uppercase tracking-wider text-amber-700 mb-1.5">Reopened {g.reopen_history.length === 1 ? 'once' : `${g.reopen_history.length} times`}</div>
          {g.reopen_history.map((h, i) => (
            <div key={i} className="text-[12px] text-slate-700 py-0.5">
              Closed {h.closed_at ? new Date(h.closed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}{h.score != null ? ` at ${Math.round(h.score)}%` : ''}, reopened {h.reopened_at ? new Date(h.reopened_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}: {h.reason}
              {h.phi_purged_at && <span className="text-slate-500"> · the PHI destruction certified at that closure stands; working material was supplied again.</span>}
            </div>
          ))}
        </div>
      )}
      <ReportPanel data={data} g={g} readOnly={readOnly} onChanged={load} setErr={setErr} />

      {g.status !== 'closed' && (
        <div className="mt-4 bg-slate-50/70 border border-slate-200 rounded-2xl px-4 py-3 text-[12px] text-slate-600">
          <span className="font-semibold text-slate-700">Close &amp; score</span> computes the results, writes the vendor&rsquo;s risk, and performs the PHI purge:
          every universe and sample file is erased, the erasure is verified, and the destruction certificate lists each artifact by name and hash. Scores, findings, and signatures survive; member data does not.
        </div>
      )}

      {(data.attestations || []).length > 0 && (
        <div className="mt-4 bg-white border border-slate-200 rounded-2xl px-4 py-3">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">Attestations</div>
          {data.attestations.map((at) => (
            <div key={at.id} className="text-[12px] text-slate-700 py-0.5 flex items-center gap-2">
              <span className="truncate">{at.tool_name}</span>
              {at.signed_at
                ? <span className="ml-auto text-[11px] font-semibold text-emerald-700">Signed · {at.signer_name} ({at.signer_title})</span>
                : <span className="ml-auto text-[11px] text-slate-400">Awaiting signature</span>}
            </div>
          ))}
        </div>
      )}
      </>)}

      {section === 'correspondence' && (
        <div className="mt-4 bg-white border border-slate-200 rounded-2xl px-4 py-3">
          <div className="flex items-center gap-2 mb-1.5">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Correspondence · {(data.correspondence || []).length}</span>
            <button onClick={() => { setNewMail(!newMail); setCorrForm(null); }}
              className="ml-auto text-[11px] font-semibold text-orange-700 hover:text-orange-900">
              {newMail ? 'Cancel' : '+ New email'}
            </button>
            <button onClick={() => { setCorrForm(corrForm ? null : { kind: 'inbound', subject: '', body: '' }); setNewMail(false); }}
              className="text-[11px] font-semibold text-orange-700 hover:text-orange-900">
              {corrForm ? 'Cancel' : '+ Log correspondence'}
            </button>
          </div>
          <div className="mb-2 text-[11px] text-slate-500">Every email this audit sent, from any door, and every reply that came back to its address, in one place. A failed send stays here marked failed; one opened in your mail program stays marked until your copy arrives.</div>
          {newMail && (
            <EmailReplyComposer mode="new" to={[g.sent_to].filter(Boolean)} subject={`${g.cycle_label || 'Audit'} · `} prior={null}
              onSend={async ({ to, cc, subject, body, files }) => {
                const fd = new FormData();
                fd.append('to', to.join(', ')); fd.append('cc', cc.join(', ')); fd.append('subject', subject); fd.append('body', body);
                for (const f of files) fd.append('files', f);
                await jfetch(`/engagements/${g.id}/correspondence/send`, { method: 'POST', body: fd });
                load();
              }}
              onClose={() => setNewMail(false)} />
          )}
          {corrForm && (
            <div className="mb-2.5 bg-slate-50 border border-slate-200 rounded-xl p-3 space-y-2">
              <div className="flex gap-2">
                <select value={corrForm.kind} onChange={(e) => setCorrForm({ ...corrForm, kind: e.target.value })}
                  className="text-[11px] border border-slate-300 rounded-lg px-2 py-1.5 bg-white">
                  <option value="inbound">Received from the vendor (paste an email)</option>
                  <option value="outbound">Sent to the vendor (record an email you sent)</option>
                  <option value="note">Internal note</option>
                </select>
                <input value={corrForm.subject} onChange={(e) => setCorrForm({ ...corrForm, subject: e.target.value })} placeholder="Subject"
                  className="flex-1 text-xs border border-slate-300 rounded-lg px-2.5 py-1.5" />
              </div>
              <textarea value={corrForm.body} onChange={(e) => setCorrForm({ ...corrForm, body: e.target.value })} rows={3}
                placeholder="Paste the email or write the note — it becomes part of the engagement record"
                className="w-full text-xs border border-slate-300 rounded-lg px-2.5 py-1.5" />
              <div className="flex items-center gap-2 flex-wrap">
                <label className="px-2.5 py-1.5 rounded-lg border border-slate-300 text-slate-600 hover:border-orange-400 text-[11px] font-semibold cursor-pointer">
                  Attach files{corrFiles.length ? ` · ${corrFiles.length}` : ''}
                  <input type="file" multiple className="hidden" onChange={(e) => { setCorrFiles([...corrFiles, ...Array.from(e.target.files || [])]); e.target.value = ''; }} />
                </label>
                {corrFiles.map((f, i) => (
                  <span key={i} className="text-[10.5px] text-slate-500 bg-slate-100 rounded px-1.5 py-0.5">{f.name}
                    <button onClick={() => setCorrFiles(corrFiles.filter((_, j) => j !== i))} className="ml-1 text-slate-400 hover:text-rose-600">✕</button>
                  </span>
                ))}
              </div>
              <button onClick={async () => {
                try {
                  const fd = new FormData();
                  fd.append('kind', corrForm.kind);
                  fd.append('subject', corrForm.subject);
                  fd.append('body', corrForm.body);
                  for (const f of corrFiles) fd.append('files', f);
                  await jfetch(`/engagements/${g.id}/correspondence`, { method: 'POST', body: fd });
                  setCorrForm(null); setCorrFiles([]); load();
                } catch (e) { setErr(e.message); }
              }} disabled={!corrForm.body.trim() && !corrFiles.length}
                className="px-3 py-1.5 rounded-lg bg-orange-500 text-white text-xs font-semibold disabled:opacity-50">Save to the record</button>
            </div>
          )}
          {(data.correspondence || []).map((c) => (
            <details key={c.id} className="py-1 border-t border-slate-50 first:border-t-0">
              <summary className="cursor-pointer text-[11.5px] text-slate-600 flex items-center gap-2">
                <span className={`px-1.5 py-px rounded-full text-[9.5px] font-bold uppercase tracking-wide ${
                  c.kind === 'letter_sent' || c.kind === 'outbound' ? 'bg-orange-50 text-orange-700' : c.kind === 'vendor_submitted' ? 'bg-sky-50 text-sky-700'
                  : c.kind === 'ack' ? 'bg-emerald-50 text-emerald-700' : c.kind === 'reminder_sent' ? 'bg-amber-50 text-amber-700' : 'bg-slate-100 text-slate-500'}`}>
                  {c.kind.replace('_', ' ')}
                </span>
                {c.status === 'failed' && <span className="px-1.5 py-px rounded-full text-[9.5px] font-bold uppercase tracking-wide bg-rose-50 text-rose-700">not sent</span>}
                {c.status === 'opened_in_mail_program' && <span className="px-1.5 py-px rounded-full text-[9.5px] font-bold uppercase tracking-wide bg-amber-50 text-amber-800">in your mail program</span>}
                <span className="truncate">{c.subject || '—'}</span>
                <span className="ml-auto text-[10.5px] text-slate-400 flex-none">
                  {(c.to_emails || c.to_email) ? `${c.to_emails || c.to_email} · ` : ''}{new Date(c.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}{c.by_name ? ` · ${c.by_name}` : ''}
                </span>
              </summary>
              {(c.email_subject || c.can_reply) && (
                <SentEmailView c={c} engagementId={g.id} onSent={load} />
              )}
              {c.body && <div className="mt-1.5 mb-1 whitespace-pre-wrap text-[11px] text-slate-600 bg-slate-50 rounded-lg px-3 py-2 max-h-56 overflow-y-auto">{c.body}</div>}
              {(data.documents || []).filter((d) => d.correspondence_id === c.id).map((d) => (
                <div key={d.id} className="mt-1 flex items-center gap-2 text-[11px] text-slate-600 bg-slate-50 rounded-lg px-2.5 py-1.5">
                  <span>📎</span><span className="truncate flex-1">{d.file_name}</span>
                  {d.purged
                    ? <span className="text-[10px] font-semibold text-slate-400">Purged</span>
                    : <a href={`${API}/api/fdr/documents/${d.id}/download`} className="text-[10.5px] font-semibold text-orange-700 hover:text-orange-900 no-underline">Download</a>}
                </div>
              ))}
            </details>
          ))}
        </div>
      )}

      {section === 'results' && data.findings.length > 0 && (
        <div className="mt-4 bg-white border border-slate-200 rounded-2xl px-4 py-3">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">Findings from this assessment · {data.findings.length}</div>
          {data.findings.map((f) => (
            <div key={f.id} className="text-[12px] text-slate-700 py-0.5 flex items-center gap-2">
              <span className={`px-1.5 py-px rounded-full text-[10px] font-semibold ${f.severity === 'critical' || f.severity === 'major' ? 'bg-rose-50 text-rose-700' : 'bg-amber-50 text-amber-700'}`}>{f.severity}</span>
              <span className="truncate">{f.title}</span>
              <span className="ml-auto text-[10.5px] text-slate-400">{f.status}</span>
            </div>
          ))}
          <div className="mt-1 text-[10.5px] text-slate-400">Worked in Findings &amp; CAPs.</div>
        </div>
      )}

      {closeWarn && createPortal(
        <div className="fixed inset-0 z-50 bg-slate-900/45 flex items-center justify-center p-4" onClick={() => setCloseWarn(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-[min(26rem,92vw)] p-5" onClick={(e) => e.stopPropagation()}>
            <div className="text-[14px] font-bold text-slate-900">{closeWarn.unanswered} elements have no verdict</div>
            <p className="mt-1.5 text-[12px] text-slate-600">Closing now scores only what has a verdict; unanswered elements stay on the record as unscored. The PHI destruction certificate is written either way.</p>
            <div className="mt-4 flex gap-2">
              <button onClick={() => doClose(true)} disabled={closing} className="px-3.5 py-2 rounded-xl bg-slate-900 text-white text-xs font-semibold disabled:opacity-50">Close anyway</button>
              <button onClick={() => setCloseWarn(null)} className="px-3.5 py-2 rounded-xl bg-white border border-slate-300 text-slate-600 text-xs font-semibold">Keep scoring</button>
            </div>
          </div>
        </div>, document.body)}

      {findingFor && (
        <FindingModal item={findingFor} engagement={g} onDone={() => { setFindingFor(null); load(); }} onCancel={() => setFindingFor(null)} />
      )}
      {sendOpen && (
        <SendModal engagement={g} onDone={() => { setSendOpen(false); setNoteMsg('Engagement letter sent.'); load(); onChanged(); }} onCancel={() => setSendOpen(false)} />
      )}
    </div>
  );
}

function FindingModal({ item, engagement, onDone, onCancel }) {
  const [form, setForm] = useState({
    title: (item.label_path ? `${item.label_path}: ` : '') + item.criteria_text.slice(0, 120),
    description: item.auditor_comments || '',
    severity: item.verdict === 'not_met' ? 'major' : 'minor',
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const save = async () => {
    setBusy(true); setErr('');
    try {
      await jfetch('/findings', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entity_id: engagement.entity_id, engagement_id: engagement.id,
          category: item.function_key, finding_type: 'deficiency', severity: form.severity,
          title: form.title, description: form.description || form.title,
        }),
      });
      onDone();
    } catch (e) { setErr(e.message); setBusy(false); }
  };
  return createPortal(
    <div className="fixed inset-0 z-50 bg-slate-900/45 flex items-center justify-center p-4" onClick={onCancel}>
      <div className="bg-white rounded-2xl shadow-2xl w-[min(32rem,94vw)] p-5" onClick={(e) => e.stopPropagation()}>
        <div className="text-[14px] font-bold text-slate-900">Log finding</div>
        {err && <div className="mt-1.5 text-[11.5px] font-semibold text-rose-600">{err}</div>}
        <div className="mt-3 space-y-2.5">
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
            className="w-full text-xs border border-slate-300 rounded-lg px-2.5 py-1.5" />
          <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3}
            placeholder="The deficiency observed and the evidence reviewed"
            className="w-full text-xs border border-slate-300 rounded-lg px-2.5 py-1.5" />
          <select value={form.severity} onChange={(e) => setForm({ ...form, severity: e.target.value })}
            className="text-xs border border-slate-300 rounded-lg px-2 py-1.5 bg-white">
            <option value="critical">Critical</option><option value="major">Major</option><option value="minor">Minor</option><option value="observation">Observation</option>
          </select>
        </div>
        <div className="mt-4 flex gap-2">
          <button onClick={save} disabled={busy || !form.title.trim()} className="px-3.5 py-2 rounded-xl bg-orange-500 text-white text-xs font-semibold disabled:opacity-50">{busy ? 'Saving…' : 'Log finding'}</button>
          <button onClick={onCancel} className="px-3.5 py-2 rounded-xl bg-white border border-slate-300 text-slate-600 text-xs font-semibold">Cancel</button>
        </div>
      </div>
    </div>, document.body);
}

// ---------------------------------------------------------------------------
// The report cycle — Draft → Rebuttal → Final, all on the engagement.
// Deterministic: the draft is built from the scored grid, the auditor edits
// every word, rebuttals land verbatim per finding, the final is frozen by
// hash and stamped on the correspondence record.
// ---------------------------------------------------------------------------
/** The report, typeset on screen the way the PDF reads — never a code box. */
export function ReportDoc({ text }) {
  const lines = String(text || '').split('\n');
  return (
    <div className="bg-white px-8 py-7">
      {lines.map((line, i) => {
        if (!line.trim()) return <div key={i} className="h-2.5" />;
        if (i === 0) return <div key={i} className="font-serif text-[21px] font-bold text-slate-900 border-b-2 border-slate-800 pb-2.5 mb-3 leading-snug">{line}</div>;
        if (/^[IVX]+\.\s/.test(line)) return <div key={i} className="mt-4 mb-1.5 text-[12.5px] font-bold text-slate-900 uppercase tracking-wide border-b border-slate-200 pb-1">{line}</div>;
        if (/^(Finding \d+|Rebuttal \d+)/.test(line)) return <div key={i} className={`mt-2.5 text-[13px] font-bold ${/Not Met/.test(line) ? 'text-rose-700' : 'text-slate-900'}`}>{line}</div>;
        if (/^(Delegated Entity|Audit Cycle|Audit Period|Issued by|Date of issuance|To|Cc):/.test(line)) return <div key={i} className="text-[12px] text-slate-500">{line}</div>;
        return <p key={i} className="font-serif text-[13px] text-slate-800 leading-relaxed my-0.5">{line}</p>;
      })}
    </div>
  );
}

/** The report as a document in a modal — read it, edit it, download it,
 *  send it. Shareable with whoever needs to review. */
function ReportModal({ title, text, editable, onTextChange, sha256, fileBase, footer, onClose }) {
  const [editing, setEditing] = useState(false);
  const download = async () => {
    const { exportAsmtReportPdf, fetchLogoDataUrl } = await import('./exportAsmtReportPdf');
    const logoDataUrl = await fetchLogoDataUrl(`${API}/api/deck-studio/branding/logo`);
    exportAsmtReportPdf({ text, fileBase, sha256, logoDataUrl });
  };
  return createPortal(
    <div className="fixed inset-0 z-50 bg-slate-900/45 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-[min(56rem,96vw)] max-h-[93vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3.5 border-b border-slate-200 flex items-center gap-3">
          <div className="text-[15px] font-bold text-slate-900">{title}</div>
          {editable && (
            <button onClick={() => setEditing((v) => !v)} className="text-[11.5px] font-semibold text-slate-500 hover:text-slate-700">
              {editing ? 'Preview the document' : 'Edit the text'}
            </button>
          )}
          <span className="ml-auto flex items-center gap-2">
            <button onClick={download} className="px-3 py-1.5 rounded-lg bg-white border border-orange-300 hover:border-orange-500 text-orange-700 text-xs font-semibold">
              Download PDF
            </button>
            <button onClick={onClose} aria-label="Close" className="w-7 h-7 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 text-sm">✕</button>
          </span>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto bg-slate-100/60 p-4">
          <div className="max-w-[46rem] mx-auto shadow-sm border border-slate-200 rounded-lg overflow-hidden">
            {editing ? (
              <textarea value={text} onChange={(e) => onTextChange(e.target.value)} rows={26}
                className="w-full text-[12px] font-mono px-5 py-4 leading-relaxed focus:outline-none" />
            ) : (
              <ReportDoc text={text} />
            )}
          </div>
          {sha256 && <div className="max-w-[46rem] mx-auto mt-1.5 text-[10px] text-slate-400 font-mono">Report fingerprint SHA-256: {sha256}</div>}
        </div>
        {footer && <div className="px-5 py-3 border-t border-slate-200 bg-slate-50/60 flex items-center gap-2 flex-wrap">{footer}</div>}
      </div>
    </div>,
    document.body
  );
}

function ReportPanel({ data, g, readOnly, onChanged, setErr }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [deadline, setDeadline] = useState('');
  const [busy, setBusy] = useState(false);
  const [showFinal, setShowFinal] = useState(false);
  const [ccIds, setCcIds] = useState(() => new Set());
  const rebutted = data.items.filter((i) => i.vendor_rebuttal);
  const pendingDispositions = rebutted.filter((i) => !i.rebuttal_disposition).length;
  const fmt = (d) => (d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '');

  const loadDraft = async () => {
    try {
      const d = await jfetch(`/engagements/${g.id}/report`);
      setDraft(d.report_draft || '');
      setOpen(true);
    } catch (e) { setErr(e.message); }
  };

  return (
    <div className="mt-4 bg-white border border-slate-200 rounded-2xl px-4 py-3">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Audit report</span>
        <span className="flex items-center gap-1 text-[10.5px]">
          <span className={`px-1.5 py-px rounded-full font-semibold ${g.report_sent_at ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-400'}`}>{g.report_sent_at ? `✓ Draft sent ${fmt(g.report_sent_at)}` : '1 · Draft'}</span>
          <span className="text-slate-200">—</span>
          <span className={`px-1.5 py-px rounded-full font-semibold ${rebutted.length ? 'bg-emerald-50 text-emerald-700' : g.report_sent_at && !g.report_final_at ? 'bg-orange-500 text-white' : 'bg-slate-100 text-slate-400'}`}>2 · Rebuttals{rebutted.length ? ` · ${rebutted.length}` : ''}</span>
          <span className="text-slate-200">—</span>
          <span className={`px-1.5 py-px rounded-full font-semibold ${g.report_final_at ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-400'}`}>{g.report_final_at ? `✓ Final ${fmt(g.report_final_at)}` : '3 · Final'}</span>
        </span>
        <span className="ml-auto flex items-center gap-2">
          {!readOnly && !g.report_sent_at && (
            <button onClick={loadDraft} className="text-[11.5px] font-semibold text-orange-700 hover:text-orange-900">
              {open ? 'Refresh draft' : 'Generate & review the draft'}
            </button>
          )}
          {!readOnly && g.report_sent_at && !g.report_final_at && (
            <button disabled={busy || pendingDispositions > 0}
              title={pendingDispositions ? `${pendingDispositions} rebuttal(s) need a disposition first` : 'Freeze the final report — rebuttals and dispositions included verbatim'}
              onClick={async () => {
                setBusy(true);
                try { await jfetch(`/engagements/${g.id}/report/final`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}) }); onChanged(); } catch (e) { setErr(e.message); }
                setBusy(false);
              }}
              className="px-3 py-1.5 rounded-lg bg-slate-900 text-white text-[11.5px] font-semibold disabled:opacity-40">
              {busy ? 'Issuing…' : 'Issue the final report'}
            </button>
          )}
          {g.report_final_at && (
            <button onClick={() => setShowFinal((v) => !v)} className="text-[11.5px] font-semibold text-slate-500 hover:text-slate-700">
              {showFinal ? 'Hide final' : 'View final'}
            </button>
          )}
          {!readOnly && (
            <label className="text-[11.5px] font-semibold text-slate-500 hover:text-slate-700 cursor-pointer"
              title="Save the report document as it went out (or came back signed) — it lands on Correspondence with the file attached">
              File the report (upload)
              <input type="file" className="hidden" onChange={async (e) => {
                const f = e.target.files?.[0]; e.target.value = '';
                if (!f) return;
                try {
                  const fd = new FormData();
                  fd.append('kind', 'outbound');
                  fd.append('subject', `Audit report — filed copy (${g.report_final_at ? 'final' : 'draft'})`);
                  fd.append('body', 'Report document filed to the record.');
                  fd.append('files', f);
                  await jfetch(`/engagements/${g.id}/correspondence`, { method: 'POST', body: fd });
                  onChanged();
                } catch (ex) { setErr(ex.message); }
              }} />
            </label>
          )}
        </span>
      </div>

      {open && !g.report_sent_at && (
        <ReportModal
          title="Draft audit report"
          text={draft}
          editable
          onTextChange={setDraft}
          fileBase={`DRAFT Audit Report ${g.cycle_label} - ${g.entity_name || 'vendor'}`}
          onClose={() => setOpen(false)}
          footer={<>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Rebuttal deadline</div>
              <input type="date" value={deadline} onChange={(e) => setDeadline(e.target.value)}
                className="text-xs border border-slate-300 rounded-lg px-2.5 py-1.5" />
            </div>
            <div className="min-w-[16rem]"><CcPicker ccIds={ccIds} setCcIds={setCcIds} /></div>
            <button disabled={busy || draft.trim().length < 200}
              onClick={async () => {
                setBusy(true);
                try {
                  await jfetch(`/engagements/${g.id}/report/send`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ report_draft: draft, rebuttal_deadline: deadline || null, cc_user_ids: [...ccIds] }) });
                  setOpen(false); onChanged();
                } catch (e) { setErr(e.message); }
                setBusy(false);
              }}
              className="px-3.5 py-2 rounded-xl bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold disabled:opacity-50">
              {busy ? 'Sending…' : 'Send to the vendor for rebuttal'}
            </button>
            <button disabled={busy || draft.trim().length < 200}
              title="You deliver the draft yourself — download the PDF, send it your way; this records it and opens the response round"
              onClick={async () => {
                setBusy(true);
                try {
                  await jfetch(`/engagements/${g.id}/report/send`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ report_draft: draft, rebuttal_deadline: deadline || null, manual: true }) });
                  setOpen(false); onChanged();
                } catch (e) { setErr(e.message); }
                setBusy(false);
              }}
              className="px-3.5 py-2 rounded-xl bg-white border border-slate-300 hover:border-slate-400 text-slate-600 text-xs font-semibold disabled:opacity-50">
              Mark as sent outside the platform
            </button>
          </>}
        />
      )}
      {g.report_sent_at && !g.report_final_at && g.report_draft && (
        <div className="mt-2">
          <button onClick={() => setShowFinal((v) => !v)} className="text-[11.5px] font-semibold text-slate-500 hover:text-slate-700">
            {showFinal ? 'Hide the draft' : 'View the sent draft'}
          </button>
        </div>
      )}
      {showFinal && g.report_sent_at && !g.report_final_at && (
        <ReportModal
          title="Draft audit report (as sent)"
          text={g.report_draft}
          fileBase={`DRAFT Audit Report ${g.cycle_label} - ${g.entity_name || 'vendor'}`}
          onClose={() => setShowFinal(false)}
        />
      )}

      {g.report_sent_at && !g.report_final_at && (
        <div className="mt-3">
          {rebutted.length === 0 && <div className="text-[11.5px] text-slate-400">Awaiting the vendor&rsquo;s responses{g.rebuttal_deadline ? ` — due ${g.rebuttal_deadline.slice(0, 10)}` : ''}. You can issue the final without them once the deadline passes.</div>}
          {/* MANUAL PATH: the vendor responded by email or phone — record it
              here, same fields, provenance marked staff-recorded. */}
          {data.items.filter((i) => (i.verdict === 'not_met' || i.verdict === 'partially_met') && !i.vendor_rebuttal).map((i) => (
            <div key={`rec-${i.id}`} className="py-2 border-t border-slate-50 first:border-t-0">
              <div className="text-[12px] text-slate-800 leading-snug">{i.label_path ? `${i.label_path} — ` : ''}{i.criteria_text.slice(0, 140)}</div>
              <RecordResponseForm item={i} onDone={onChanged} setErr={setErr} />
            </div>
          ))}
          {rebutted.map((i) => (
            <div key={i.id} className="py-2 border-t border-slate-50 first:border-t-0">
              <div className="text-[12px] text-slate-800 leading-snug">{i.label_path ? `${i.label_path} — ` : ''}{i.criteria_text.slice(0, 140)}</div>
              {(data.exchanges || []).filter((x) => x.item_id === i.id).map((x, xi) => (
                <div key={xi} className={`mt-1 border-l-2 rounded-r-lg px-2.5 py-1.5 text-[11.5px] text-slate-700 ${x.author === 'vendor' ? 'bg-sky-50/70 border-sky-300' : x.position === 'accepted' ? 'bg-emerald-50/70 border-emerald-300' : 'bg-rose-50/60 border-rose-300'}`}>
                  <span className={`text-[9.5px] font-bold uppercase tracking-wider mr-1.5 ${x.author === 'vendor' ? 'text-sky-600' : x.position === 'accepted' ? 'text-emerald-600' : 'text-rose-600'}`}>
                    {x.author === 'vendor'
                      ? (x.position === 'agree' ? 'Management agrees' : x.position === 'partially_agree' ? 'Management partially agrees' : 'Management disagrees')
                      : (x.position === 'accepted' ? 'You — accepted' : 'You — not accepted')}
                  </span>
                  {x.body}
                  {x.mgmt_action && <div className="mt-1 text-[11px] text-slate-600">Committed action: {x.mgmt_action}{x.mgmt_owner ? ` — ${x.mgmt_owner}` : ''}{x.mgmt_target_date ? ` — by ${String(x.mgmt_target_date).slice(0, 10)}` : ''}</div>}
                </div>
              ))}
              {!i.rebuttal_disposition && (
                <div className="mt-1 text-[10.5px] text-amber-700">The vendor&rsquo;s latest turn awaits your disposition — the final cannot issue until you rule on it. They may counter after each ruling.</div>
              )}
              {i.rebuttal_disposition && <RecordResponseForm item={i} counter onDone={onChanged} setErr={setErr} />}
              <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                {['accepted', 'rejected'].map((d) => (
                  <button key={d} onClick={() => patchDisposition(i.id, d)}
                    className={`px-2.5 py-1 rounded-lg text-[11px] font-semibold ring-1 ${i.rebuttal_disposition === d
                      ? (d === 'accepted' ? 'bg-emerald-50 text-emerald-700 ring-emerald-200' : 'bg-rose-50 text-rose-700 ring-rose-200')
                      : 'bg-white text-slate-400 ring-slate-200 hover:text-slate-600'}`}>
                    {d === 'accepted' ? 'Accept — amend the finding' : 'Reject — the finding stands'}
                  </button>
                ))}
                <input defaultValue={i.rebuttal_note || ''} placeholder="Disposition note (goes in the final)"
                  onBlur={(e) => e.target.value !== (i.rebuttal_note || '') && patchDisposition(i.id, i.rebuttal_disposition, e.target.value)}
                  className="flex-1 min-w-[14rem] text-[11px] border border-slate-200 rounded-lg px-2.5 py-1.5" />
              </div>
            </div>
          ))}
        </div>
      )}

      {showFinal && g.report_final && (
        <div className="mt-3">
          <div className="max-h-96 overflow-y-auto bg-slate-50 border border-slate-200 rounded-xl p-4 text-[11.5px] text-slate-700 whitespace-pre-wrap leading-relaxed font-mono">{g.report_final}</div>
          <div className="mt-1 text-[10px] text-slate-400 font-mono">SHA-256 {g.report_final_sha256}</div>
        </div>
      )}
    </div>
  );

  async function patchDisposition(itemId, disposition, note) {
    try {
      const body = { rebuttal_disposition: disposition || null };
      if (note !== undefined) body.rebuttal_note = note;
      await jfetch(`/engagements/items/${itemId}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      onChanged();
    } catch (e) { setErr(e.message); }
  }
}

/** Staff records a vendor response received outside the platform. */
function RecordResponseForm({ item, counter = false, onDone, setErr }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ position: '', response: '', action: '', owner: '', target_date: '' });
  const [busy, setBusy] = useState(false);
  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className="mt-1 text-[11px] font-semibold text-slate-500 hover:text-slate-700">
        {counter ? '+ Record the vendor’s counter (received outside the platform)' : '+ Record the vendor’s response (received outside the platform)'}
      </button>
    );
  }
  const agreeing = f.position === 'agree' || f.position === 'partially_agree';
  return (
    <div className="mt-1.5 bg-slate-50 border border-slate-200 rounded-xl p-3 space-y-2">
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mr-1">Their position:</span>
        {[['agree', 'Agrees'], ['partially_agree', 'Partially agrees'], ['disagree', 'Disagrees']].map(([k, label]) => (
          <button key={k} onClick={() => setF({ ...f, position: k })}
            className={`px-2.5 py-1 rounded-lg text-[11px] font-semibold ring-1 ${f.position === k
              ? (k === 'disagree' ? 'bg-rose-50 text-rose-700 ring-rose-200' : 'bg-emerald-50 text-emerald-700 ring-emerald-200')
              : 'bg-white text-slate-400 ring-slate-200 hover:text-slate-600'}`}>{label}</button>
        ))}
        <button onClick={() => setOpen(false)} className="ml-auto text-[10.5px] text-slate-400 hover:text-slate-600">cancel</button>
      </div>
      <textarea rows={2} value={f.response} onChange={(e) => setF({ ...f, response: e.target.value })}
        placeholder="Their response, as received — paste the email text or transcribe the call"
        className="w-full text-xs border border-slate-300 rounded-lg px-2.5 py-1.5" />
      {agreeing && (
        <div className="flex gap-2 flex-wrap">
          <input value={f.action} onChange={(e) => setF({ ...f, action: e.target.value })} placeholder="Committed corrective action"
            className="flex-1 min-w-[14rem] text-xs border border-slate-300 rounded-lg px-2.5 py-1.5" />
          <input value={f.owner} onChange={(e) => setF({ ...f, owner: e.target.value })} placeholder="Owner"
            className="w-40 text-xs border border-slate-300 rounded-lg px-2.5 py-1.5" />
          <input type="date" value={f.target_date} onChange={(e) => setF({ ...f, target_date: e.target.value })}
            className="text-xs border border-slate-300 rounded-lg px-2.5 py-1.5" />
        </div>
      )}
      <button disabled={busy || !f.position || !f.response.trim()}
        onClick={async () => {
          setBusy(true);
          try {
            await jfetch(`/engagements/items/${item.id}/record-response`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...f, target_date: f.target_date || null }) });
            setOpen(false); onDone();
          } catch (e) { setErr(e.message); }
          setBusy(false);
        }}
        className="px-3 py-1.5 rounded-lg bg-slate-900 text-white text-[11.5px] font-semibold disabled:opacity-50">
        {busy ? 'Recording…' : 'Record to the exchange'}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Universes & samples — named slots the vendor cannot mistake. Scrubber and
// Reporter universes go through those tools by staff, as today; every other
// universe is simply delivered here. Sample requests carry the universe
// file's hash so the record proves which exact file was sampled. Everything
// PHI is purged with the certificate at close.
// ---------------------------------------------------------------------------
function UniversePanel({ data, g, readOnly, onChanged, setErr }) {
  const [presets, setPresets] = useState([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ label: '', source_kind: 'file', instructions: '' });
  const [sampleFor, setSampleFor] = useState(null);
  const [sampleText, setSampleText] = useState('');
  const [feedbackFor, setFeedbackFor] = useState(null);
  const [feedbackText, setFeedbackText] = useState('');
  useEffect(() => { jfetch('/assessment-universe-presets').then((d) => setPresets(d.presets)).catch(() => {}); }, []);

  const slots = data.universe_requests || [];
  const samples = data.sample_requests || [];
  const docsFor = (field, id) => (data.documents || []).filter((d) => d[field] === id);
  if (!slots.length && readOnly) return null;

  const KIND = { scrubber: ['Scrubber', 'bg-orange-50 text-orange-700'], reporter: ['Reporter', 'bg-indigo-50 text-indigo-700'], file: ['Delivered as-is', 'bg-slate-100 text-slate-500'] };

  return (
    <div className="mt-4 bg-white border border-slate-200 rounded-2xl px-4 py-3">
      <div className="flex items-center gap-2 mb-1.5">
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Universes &amp; samples · {slots.length}</span>
        {!readOnly && (
          <button onClick={() => setAdding((v) => !v)} className="ml-auto text-[11px] font-semibold text-orange-700 hover:text-orange-900">
            {adding ? 'Cancel' : '+ Request a universe'}
          </button>
        )}
      </div>
      {adding && (
        <div className="mb-2.5 bg-slate-50 border border-slate-200 rounded-xl p-3 space-y-2">
          <div className="flex gap-2 flex-wrap">
            <select value="" onChange={(e) => {
              const p = presets.find((x) => x.label === e.target.value);
              if (p) setForm({ ...form, label: p.label, source_kind: p.source_kind });
            }} className="text-[11px] border border-slate-300 rounded-lg px-2 py-1.5 bg-white">
              <option value="">Pick a common universe…</option>
              {presets.map((p) => <option key={p.label} value={p.label}>{p.label}</option>)}
            </select>
            <input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })}
              placeholder="…or name it (e.g. ODAG Table 1 — Organization Determinations)"
              className="flex-1 min-w-[16rem] text-xs border border-slate-300 rounded-lg px-2.5 py-1.5" />
          </div>
          <input value={form.instructions} onChange={(e) => setForm({ ...form, instructions: e.target.value })}
            placeholder="Instructions the vendor will see (period, format, what to include)"
            className="w-full text-xs border border-slate-300 rounded-lg px-2.5 py-1.5" />
          <button onClick={async () => {
            try {
              await jfetch(`/engagements/${g.id}/universe-requests`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
              setAdding(false); setForm({ label: '', source_kind: 'file', instructions: '' }); onChanged();
            } catch (e) { setErr(e.message); }
          }} disabled={!form.label.trim()}
            className="px-3 py-1.5 rounded-lg bg-orange-500 text-white text-xs font-semibold disabled:opacity-50">
            Add — the vendor sees it in their portal
          </button>
        </div>
      )}
      {slots.map((u) => {
        const files = docsFor('universe_request_id', u.id);
        const mySamples = samples.filter((s) => s.universe_request_id === u.id);
        return (
          <div key={u.id} className="py-2 border-t border-slate-50 first:border-t-0">
            <div className="flex items-center gap-2 text-[12.5px] text-slate-800">
              <span className="font-semibold">{u.label}</span>
              <span className={`px-1.5 py-px rounded text-[9.5px] font-semibold ${KIND[u.source_kind][1]}`}>{KIND[u.source_kind][0]}</span>
              <span className={`px-1.5 py-px rounded-full text-[10px] font-semibold ${u.status === 'received' ? 'bg-emerald-50 text-emerald-700' : u.feedback ? 'bg-rose-50 text-rose-700' : 'bg-amber-50 text-amber-700'}`}>
                {u.status === 'received' ? 'Received' : u.feedback ? 'Returned to vendor' : 'Awaiting vendor'}
              </span>
              <span className="ml-auto flex items-center gap-2">
                {u.status === 'received' && !readOnly && (
                  <button onClick={() => { setFeedbackFor(feedbackFor === u.id ? null : u.id); setFeedbackText(''); }}
                    title="You validated it through the Scrubber and found errors — send it back with the message"
                    className="text-[11px] font-semibold text-rose-600 hover:text-rose-800">Return with feedback</button>
                )}
                {files.length > 0 && !readOnly && (
                  <button onClick={() => { setSampleFor(sampleFor === u.id ? null : u.id); setSampleText(''); }}
                    className="text-[11px] font-semibold text-orange-700 hover:text-orange-900">Request case files</button>
                )}
                {!readOnly && (
                  <label className="text-[11px] font-semibold text-slate-500 hover:text-slate-700 cursor-pointer"
                    title="The vendor sent it outside the portal (email, SFTP) — file it into this slot yourself">
                    Attach file
                    <input type="file" className="hidden" onChange={async (e) => {
                      const f = e.target.files?.[0]; e.target.value = '';
                      if (!f) return;
                      try {
                        const fd = new FormData(); fd.append('file', f);
                        await jfetch(`/engagements/universe-requests/${u.id}/upload`, { method: 'POST', body: fd });
                        onChanged();
                      } catch (ex) { setErr(ex.message); }
                    }} />
                  </label>
                )}
                {!files.length && !readOnly && (
                  <button onClick={async () => { try { await jfetch(`/engagements/universe-requests/${u.id}`, { method: 'DELETE' }); onChanged(); } catch (e) { setErr(e.message); } }}
                    className="text-[10.5px] text-slate-300 hover:text-slate-500">remove</button>
                )}
              </span>
            </div>
            {u.instructions && <div className="text-[11px] text-slate-400 mt-0.5">{u.instructions}</div>}
            {u.feedback && u.status !== 'received' && (
              <div className="mt-1 text-[11px] text-rose-700 bg-rose-50 rounded-lg px-2.5 py-1.5">Returned: “{u.feedback}”</div>
            )}
            {feedbackFor === u.id && (
              <div className="mt-1.5 bg-slate-50 border border-slate-200 rounded-xl p-3">
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">What was wrong and what to fix — the vendor sees this on the slot and re-uploads</div>
                <textarea value={feedbackText} onChange={(e) => setFeedbackText(e.target.value)} rows={3}
                  placeholder="e.g. Scrubber validation failed: 14 records missing the received date; column headers do not match the ODAG Table 1 layout. Correct and resubmit the full universe."
                  className="w-full text-xs border border-slate-300 rounded-lg px-2.5 py-1.5" />
                <button onClick={async () => {
                  try {
                    await jfetch(`/engagements/universe-requests/${u.id}/feedback`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ message: feedbackText }) });
                    setFeedbackFor(null); onChanged();
                  } catch (e) { setErr(e.message); }
                }} disabled={feedbackText.trim().length < 5}
                  className="mt-1.5 px-3 py-1.5 rounded-lg bg-rose-600 text-white text-xs font-semibold disabled:opacity-50">Return to the vendor</button>
              </div>
            )}
            {files.map((d) => (
              <div key={d.id} className="mt-1 flex items-center gap-2 text-[11.5px] text-slate-600 bg-slate-50 rounded-lg px-2.5 py-1.5">
                <span>📎</span>
                <span className="truncate flex-1">{d.file_name}</span>
                <span className="text-[10px] text-slate-400" title={`sha256 ${d.content_hash}`}>{(d.content_hash || '').slice(0, 12)}…</span>
                {d.purged
                  ? <span className="text-[10.5px] font-semibold text-slate-400" title="Erased at close — the destruction certificate holds the hash">Purged</span>
                  : <a href={`${API}/api/fdr/documents/${d.id}/download`} className="text-[11px] font-semibold text-orange-700 hover:text-orange-900 no-underline">Download</a>}
              </div>
            ))}
            {sampleFor === u.id && (
              <div className="mt-1.5 bg-slate-50 border border-slate-200 rounded-xl p-3">
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Case identifiers — one per line. The request records the universe file&rsquo;s hash, proving which file was sampled.</div>
                <textarea value={sampleText} onChange={(e) => setSampleText(e.target.value)} rows={4}
                  className="w-full text-xs font-mono border border-slate-300 rounded-lg px-2.5 py-1.5" />
                <button onClick={async () => {
                  try {
                    await jfetch(`/engagements/universe-requests/${u.id}/samples`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ identifiers: sampleText }) });
                    setSampleFor(null); onChanged();
                  } catch (e) { setErr(e.message); }
                }} disabled={!sampleText.trim()}
                  className="mt-1.5 px-3 py-1.5 rounded-lg bg-orange-500 text-white text-xs font-semibold disabled:opacity-50">Send the sample request</button>
              </div>
            )}
            {mySamples.map((s) => {
              const sFiles = docsFor('sample_request_id', s.id);
              return (
                <div key={s.id} className="mt-1.5 ml-3 border-l-2 border-slate-100 pl-2.5">
                  <div className="text-[11.5px] text-slate-600 flex items-center gap-2">
                    <span className="font-semibold">Sample · {s.identifiers.split('\n').filter(Boolean).length} case(s)</span>
                    {s.universe_sha256 && <span className="text-[9.5px] text-slate-400" title={`drawn from universe sha256 ${s.universe_sha256}`}>from {s.universe_sha256.slice(0, 12)}…</span>}
                    <span className={`px-1.5 py-px rounded-full text-[10px] font-semibold ${sFiles.length ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                      {sFiles.length ? `${sFiles.length} file(s) received` : 'Awaiting vendor'}
                    </span>
                    {!readOnly && (
                      <label className="text-[10.5px] font-semibold text-slate-500 hover:text-slate-700 cursor-pointer"
                        title="The vendor sent the case files outside the portal — file them here yourself">
                        Attach file
                        <input type="file" className="hidden" onChange={async (e) => {
                          const f = e.target.files?.[0]; e.target.value = '';
                          if (!f) return;
                          try {
                            const fd = new FormData(); fd.append('file', f);
                            await jfetch(`/engagements/sample-requests/${s.id}/upload`, { method: 'POST', body: fd });
                            onChanged();
                          } catch (ex) { setErr(ex.message); }
                        }} />
                      </label>
                    )}
                  </div>
                  {sFiles.map((d) => (
                    <div key={d.id} className="mt-1 flex items-center gap-2 text-[11.5px] text-slate-600 bg-slate-50 rounded-lg px-2.5 py-1.5">
                      <span>📎</span><span className="truncate flex-1">{d.file_name}</span>
                      {d.purged
                        ? <span className="text-[10.5px] font-semibold text-slate-400">Purged</span>
                        : <a href={`${API}/api/fdr/documents/${d.id}/download`} className="text-[11px] font-semibold text-orange-700 hover:text-orange-900 no-underline">Download</a>}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        );
      })}
      {!slots.length && <div className="text-[11.5px] text-slate-400 py-1">No universes requested. Add one and the vendor sees a clearly labeled upload slot in their portal.</div>}
      {slots.length > 0 && <div className="mt-1.5 text-[10.5px] text-slate-400">Universe and sample files are PHI working material — erased with a destruction certificate when the assessment closes. Scrubber and Reporter universes are run through those tools by your team as today.</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tool library manager — import the organization's real workbooks; edit
// anything; system baseline rows are read-only Precision content.
// ---------------------------------------------------------------------------
function ToolsManager({ tools, onChanged, onBack }) {
  const [openTool, setOpenTool] = useState(null);
  const [elements, setElements] = useState([]);
  const [importForm, setImportForm] = useState({ function_key: 'claims', name: '' });
  const [busyMsg, setBusyMsg] = useState('');
  const [err, setErr] = useState('');
  const [editingEl, setEditingEl] = useState(null);
  const [editText, setEditText] = useState('');
  const [renaming, setRenaming] = useState(null);
  const [renameText, setRenameText] = useState('');
  const fileRef = useRef(null);

  // Any edit works on any tool: editing a Precision baseline silently makes
  // it the org's own copy (the server handles it) — surface that honestly.
  const afterEdit = (r, toolId) => {
    if (r?.copied) {
      setBusyMsg('That was a Precision baseline — you are now editing your own copy; the baseline stays pristine for everyone else.');
      setTimeout(() => setBusyMsg(''), 9000);
      setOpenTool(r.tool_id || null);
      if (r.tool_id) loadElements(r.tool_id);
    } else if (toolId) loadElements(toolId);
    onChanged();
  };

  const loadElements = useCallback((toolId) => {
    jfetch(`/assessment-tools/${toolId}/elements`).then((d) => setElements(d.elements)).catch((e) => setErr(e.message));
  }, []);

  const doImport = async (file) => {
    if (!file) return;
    setBusyMsg(`Ingesting ${file.name}…`); setErr('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('function_key', importForm.function_key);
      fd.append('name', importForm.name || file.name.replace(/\.[^.]+$/, ''));
      const d = await jfetch('/assessment-tools/import', { method: 'POST', body: fd });
      setBusyMsg(`Imported ${d.tool.name}: ${d.tool.element_count} elements from ${d.sections.join(', ')}.`);
      setImportForm({ function_key: 'claims', name: '' });
      onChanged();
      setTimeout(() => setBusyMsg(''), 8000);
    } catch (e) { setBusyMsg(''); setErr(e.message); }
  };

  return (
    <div className="p-8 pt-4 max-w-6xl mx-auto w-full">
      <button onClick={onBack} className="text-[11.5px] font-semibold text-slate-500 hover:text-slate-700">← All vendors</button>
      <div className="mt-2 flex items-baseline justify-between flex-wrap gap-2">
        <div>
          <div className="text-[16px] font-bold text-slate-900">Audit tool library</div>
          <div className="text-[11.5px] text-slate-500 mt-0.5">Your real audit tools, verbatim. Import a workbook and it becomes the working instrument — editable any time; open assessments keep the version they started with.</div>
        </div>
        {busyMsg && <span className="text-[11.5px] font-semibold text-emerald-700">{busyMsg}</span>}
        {err && <span className="text-[11.5px] font-semibold text-rose-600">{err}</span>}
      </div>

      <div className="mt-3 bg-slate-50 border border-slate-200 rounded-2xl px-4 py-3 flex items-end gap-2.5 flex-wrap">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">Function</div>
          <select value={importForm.function_key} onChange={(e) => setImportForm({ ...importForm, function_key: e.target.value })}
            className="text-xs border border-slate-300 rounded-lg px-2 py-1.5 bg-white">
            {Object.entries(FUNCTION_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        <div className="flex-1 min-w-[14rem]">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">Tool name (optional — defaults to the file name)</div>
          <input value={importForm.name} onChange={(e) => setImportForm({ ...importForm, name: e.target.value })}
            placeholder="e.g. Claims Audit Tool 2026"
            className="w-full text-xs border border-slate-300 rounded-lg px-2.5 py-1.5" />
        </div>
        <input type="file" ref={fileRef} accept=".xlsx,.xls" className="hidden"
          onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; doImport(f); }} />
        <button onClick={() => fileRef.current?.click()}
          className="px-3.5 py-2 rounded-xl bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold">
          Import workbook
        </button>
      </div>

      <div className="mt-3 space-y-2">
        {[...tools.filter((t) => t.active), ...tools.filter((t) => !t.active)].map((t) => (
          <div key={t.id} className={`bg-white border border-slate-200 rounded-2xl px-4 py-3 ${t.active ? '' : 'opacity-60'}`}>
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => { const next = openTool === t.id ? null : t.id; setOpenTool(next); if (next) loadElements(t.id); }}>
              {renaming === t.id ? (
                <input autoFocus value={renameText} onChange={(ev) => setRenameText(ev.target.value)}
                  onClick={(ev) => ev.stopPropagation()}
                  onKeyDown={async (ev) => {
                    if (ev.key === 'Enter') {
                      const r = await jfetch(`/assessment-tools/${t.id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name: renameText }) });
                      setRenaming(null); afterEdit(r, t.id);
                    }
                    if (ev.key === 'Escape') setRenaming(null);
                  }}
                  className="text-[13px] font-semibold border border-orange-300 rounded px-1.5 py-0.5 min-w-[18rem]" />
              ) : (
                <span className="text-[13px] font-semibold text-slate-900">{t.name}</span>
              )}
              <span className="px-1.5 py-px rounded bg-slate-100 text-[10px] font-semibold text-slate-500">{FUNCTION_LABELS[t.function_key] || t.function_key}</span>
              <span className="text-[11px] text-slate-400">
                {t.element_count} elements{t.source_file ? ` · from ${t.source_file}` : ''}
                {t.organization_id ? (t.source_tool_id ? ' · your copy of the Precision baseline' : '') : ' · Precision baseline (editing makes your own copy)'}
                {!t.active && ' · retired'}
              </span>
              <span className="ml-auto flex items-center gap-2" onClick={(ev) => ev.stopPropagation()}>
                {renaming !== t.id && (
                  <button onClick={() => { setRenaming(t.id); setRenameText(t.name); }} className="text-[10.5px] font-semibold text-slate-300 hover:text-slate-500">rename</button>
                )}
                <button onClick={async () => {
                  const r = await jfetch(`/assessment-tools/${t.id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ active: !t.active }) });
                  afterEdit(r, null);
                }} className="text-[10.5px] font-semibold text-slate-300 hover:text-slate-500">{t.active ? 'retire' : 'restore'}</button>
                <span className="text-slate-300 text-xs">{openTool === t.id ? '▴' : '▾'}</span>
              </span>
            </div>
            {openTool === t.id && (
              <div className="mt-2.5 max-h-96 overflow-y-auto border-t border-slate-100 pt-2">
                {elements.map((e) => (
                  <div key={e.id} className={`py-1 flex items-start gap-2 text-[11.5px] ${e.active ? 'text-slate-700' : 'text-slate-300 line-through'}`}>
                    {e.label_path && <span className="flex-none px-1 py-px rounded bg-slate-100 text-[9.5px] font-semibold text-slate-500">{e.label_path}</span>}
                    {editingEl === e.id ? (
                      <input autoFocus value={editText} onChange={(ev) => setEditText(ev.target.value)}
                        onKeyDown={async (ev) => {
                          if (ev.key === 'Enter') {
                            const r = await jfetch(`/assessment-tools/elements/${e.id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ criteria_text: editText }) });
                            setEditingEl(null); afterEdit(r, t.id);
                          }
                          if (ev.key === 'Escape') setEditingEl(null);
                        }}
                        className="flex-1 text-[11.5px] border border-orange-300 rounded px-1.5 py-0.5" />
                    ) : (
                      <span className="flex-1 leading-snug">{e.criteria_text}</span>
                    )}
                    {editingEl !== e.id && (
                      <span className="flex-none flex gap-1.5 items-center">
                        <button
                          title="What the vendor must provide — click to change"
                          onClick={async () => {
                            const next = (e.response_mode || 'text') === 'text' ? 'attachment' : e.response_mode === 'attachment' ? 'both' : 'text';
                            const r = await jfetch(`/assessment-tools/elements/${e.id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ response_mode: next }) });
                            afterEdit(r, t.id);
                          }}
                          className={`px-1 py-px rounded text-[9.5px] font-semibold ${
                            e.response_mode === 'attachment' ? 'bg-sky-50 text-sky-700' : e.response_mode === 'both' ? 'bg-violet-50 text-violet-700' : 'bg-slate-50 text-slate-400'}`}>
                          {e.response_mode === 'attachment' ? 'File' : e.response_mode === 'both' ? 'Text+File' : 'Text'}
                        </button>
                        <button onClick={() => { setEditingEl(e.id); setEditText(e.criteria_text); }} className="text-[10px] text-slate-300 hover:text-slate-500">edit</button>
                        <button onClick={async () => { const r = await jfetch(`/assessment-tools/elements/${e.id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ active: !e.active }) }); afterEdit(r, t.id); }}
                          className="text-[10px] text-slate-300 hover:text-slate-500">{e.active ? 'remove' : 'restore'}</button>
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
        {tools.length === 0 && <div className="text-center text-xs text-slate-400 py-8">No tools yet — import your first audit tool workbook above.</div>}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The tab: vendor board → engagement / tools / legacy.
// ---------------------------------------------------------------------------
export default function Assessments({ onOpenEntity = null }) {
  const [board, setBoard] = useState(null);
  const [tools, setTools] = useState([]);
  const [view, setView] = useState('board'); // board | engagement | tools | legacy
  const [engagementId, setEngagementId] = useState(null);
  const [startFor, setStartFor] = useState(null);
  const [cycle, setCycle] = useState('all'); // audit year / CY filter
  const [err, setErr] = useState('');

  const load = useCallback(() => {
    Promise.all([jfetch('/engagements'), jfetch('/assessment-tools')])
      .then(([b, t]) => { setBoard(b.vendors); setTools(t.tools); })
      .catch((e) => setErr(e.message));
  }, []);
  useEffect(() => { load(); }, [load]);

  // The one centralized place: annual attestations live BEHIND the board,
  // not as a tab of their own. (Past-audits and legacy-assessment doors were
  // retired 2026-08-22 — the archives confused more than they served.)
  if (view === 'attestations') {
    return (
      <div className="flex-1 min-h-0 flex flex-col overflow-y-auto">
        <div className="px-8 pt-4">
          <button onClick={() => setView('board')} className="text-[11.5px] font-semibold text-slate-500 hover:text-slate-700">← Back to assessments</button>
        </div>
        <AttestationDashboard onOpenEntity={onOpenEntity} />
      </div>
    );
  }
  if (view === 'tools') {
    return <div className="flex-1 min-h-0 overflow-y-auto"><ToolsManager tools={tools} onChanged={load} onBack={() => setView('board')} /></div>;
  }
  if (view === 'engagement' && engagementId) {
    return <div className="flex-1 min-h-0 overflow-y-auto"><EngagementView engagementId={engagementId} onBack={() => { setView('board'); load(); }} onChanged={load} /></div>;
  }

  return (
    <div className="flex-1 min-h-0 overflow-y-auto">
      <div className="p-8 pt-4 max-w-7xl mx-auto w-full">
        <div className="flex items-baseline justify-between flex-wrap gap-2">
          <p className="text-[11.5px] text-slate-400">One assessment per vendor per cycle. The audit tools assemble from what each vendor is delegated to do; scoring, findings, and the PHI destruction record all live on the engagement.</p>
          <div className="flex items-center gap-3">
            <select value={cycle} onChange={(e) => setCycle(e.target.value)}
              title="Filter the board by audit cycle / contract year"
              className="text-[11.5px] border border-slate-200 rounded-lg px-2 py-1 bg-white text-slate-600">
              <option value="all">All cycles</option>
              {[...new Set((board || []).flatMap((v) => v.engagements.map((e) => e.cycle_label)))].sort().reverse().map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
            <button onClick={() => setView('attestations')} className="text-[11.5px] font-semibold text-orange-700 hover:text-orange-900">Annual attestations</button>
            <button onClick={() => setView('tools')} className="text-[11.5px] font-semibold text-orange-700 hover:text-orange-900">Manage tools · {tools.length}</button>
          </div>
        </div>
        {err && <div className="mt-2 text-[11.5px] font-semibold text-rose-600">{err}</div>}
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {(board || []).map((v) => {
            const pool = cycle === 'all' ? v.engagements : v.engagements.filter((e) => e.cycle_label === cycle);
            const latest = pool[0] || null;
            return (
              <div key={v.id} className="bg-white border border-slate-200 rounded-2xl p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="text-[13.5px] font-semibold text-slate-900">{v.name}</div>
                  {latest && <StagePill engagement={latest} />}
                </div>
                <div className="mt-1 flex flex-wrap gap-1">
                  {v.function_keys.map((k) => (
                    <span key={k} className="px-1.5 py-px rounded bg-slate-100 text-[10px] font-semibold text-slate-500">{FUNCTION_LABELS[k] || k}</span>
                  ))}
                  <button onClick={(e) => { e.stopPropagation(); setView('attestations'); }}
                    title="The CY annual attestation — managed under Annual attestations"
                    className={`px-1.5 py-px rounded text-[10px] font-semibold ${
                      ['received', 'accepted'].includes(v.attestation_status) ? 'bg-emerald-50 text-emerald-700'
                      : v.attestation_status === 'sent' ? 'bg-amber-50 text-amber-700' : 'bg-slate-50 text-slate-400'}`}>
                    CY{new Date().getFullYear()} attestation · {['received', 'accepted'].includes(v.attestation_status) ? 'signed' : v.attestation_status === 'sent' ? 'out for signature' : 'not started'}
                  </button>
                </div>
                {latest ? (
                  <div className="mt-2.5">
                    <div className="text-[11px] text-slate-500">{TYPE_LABELS[latest.audit_type]} · {latest.cycle_label} · {latest.scored_count} of {latest.item_count} scored</div>
                    <div className="mt-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                      <div className={`h-full rounded-full ${latest.status === 'closed' ? 'bg-emerald-500' : 'bg-orange-500'}`}
                        style={{ width: `${latest.item_count ? Math.round((latest.scored_count / latest.item_count) * 100) : 0}%` }} />
                    </div>
                  </div>
                ) : (
                  <div className="mt-2.5 text-[11px] text-slate-400">{cycle === 'all' ? 'No assessment yet.' : `No assessment for ${cycle}.`}</div>
                )}
                <div className="mt-3 flex items-center gap-2">
                  {latest && (
                    <button onClick={() => { setEngagementId(latest.id); setView('engagement'); }}
                      className="px-3 py-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-[11.5px] font-semibold">
                      Open
                    </button>
                  )}
                  <button onClick={() => setStartFor(v)}
                    className="px-3 py-1.5 rounded-lg bg-white border border-orange-300 hover:border-orange-500 text-orange-700 text-[11.5px] font-semibold">
                    Start assessment
                  </button>
                  {pool.length > 1 && (
                    <span className="ml-auto text-[10.5px] text-slate-400">{pool.length} in this cycle</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {startFor && (
        <StartModal vendor={startFor} tools={tools}
          onDone={(g) => { setStartFor(null); setEngagementId(g.id); setView('engagement'); load(); }}
          onCancel={() => setStartFor(null)} />
      )}
    </div>
  );
}

/**
 * Every file on this audit, the moment it lands — not at submission. The
 * vendor's uploads from the portal, the files staff filed, and where each
 * one is attached: an element, a universe or sample slot, or the audit as a
 * whole. Quarantined files are named so the vendor can be asked to resend.
 */
function VendorFilesPanel({ data }) {
  const [q, setQ] = useState('');
  const items = data.items || [];
  const itemById = new Map(items.map((i) => [i.id, i]));
  const uniById = new Map((data.universe_requests || []).map((u) => [u.id, u]));
  const all = (data.documents || []).filter((d) => d.category !== 'correspondence' && !d.correspondence_id);
  const needle = q.trim().toLowerCase();
  const docs = all
    .filter((d) => !needle || String(d.file_name || '').toLowerCase().includes(needle))
    .sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)));
  const fmtDate = (iso) => new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
  const fmtSize = (n) => (n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round((n || 0) / 1024))} KB`);
  const whereOf = (d) => {
    if (d.item_id) { const it = itemById.get(d.item_id); return it ? `On element ${it.label_path ? `${it.label_path} ` : ''}${String(it.criteria_text || '').slice(0, 70)}` : 'On an element'; }
    if (d.universe_request_id) return `Universe · ${uniById.get(d.universe_request_id)?.label || 'slot'}`;
    if (d.sample_request_id) return 'Sample case files';
    return 'Attached to the audit as a whole';
  };
  const quarantined = (d) => d.extraction_status === 'blocked_phi' || (d.purged && !d.universe_request_id && !d.sample_request_id);
  const fromVendor = all.filter((d) => d.source === 'vendor').length;
  return (
    <div className="mt-4">
      <div className="flex items-center gap-3 flex-wrap">
        <div>
          <div className="text-[13px] font-semibold text-slate-900">Files on this audit · {all.length}</div>
          <div className="text-[11.5px] text-slate-500">{fromVendor} sent by the vendor through the portal{all.length - fromVendor ? `, ${all.length - fromVendor} filed by staff` : ''}. Each appears here as soon as it is uploaded.</div>
        </div>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search file names"
          className="ml-auto text-[12px] border border-slate-200 rounded-lg px-2.5 py-1.5 bg-white min-w-[200px]" />
      </div>
      {!all.length ? (
        <div className="mt-3 bg-white border border-dashed border-slate-300 rounded-2xl px-4 py-6 text-center text-[12.5px] text-slate-500">
          Nothing has been uploaded yet. Files the vendor attaches in the portal show here right away.
        </div>
      ) : (
        <div className="mt-3 bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <div className="grid grid-cols-[minmax(220px,1fr)_minmax(180px,1fr)_120px_150px] gap-3 px-4 py-2 text-[10px] font-bold uppercase tracking-wider text-slate-400 border-b border-slate-200">
            <div>File</div><div>Where it is attached</div><div>From</div><div>Received</div>
          </div>
          {docs.map((d) => (
            <div key={d.id} className="grid grid-cols-[minmax(220px,1fr)_minmax(180px,1fr)_120px_150px] gap-3 px-4 py-2.5 border-b border-slate-100 items-start text-[12px]">
              <div className="min-w-0">
                {quarantined(d) ? (
                  <span className="text-slate-500 line-through break-words">{d.file_name}</span>
                ) : (
                  <a href={`${API}/api/fdr/documents/${d.id}/download`} className="font-semibold text-orange-700 hover:text-orange-900 break-words">{d.file_name}</a>
                )}
                <div className="text-[11px] text-slate-400">{fmtSize(d.file_size_bytes)}{quarantined(d) ? ' · quarantined, the file tripped the PHI screen and was destroyed; ask the vendor to resend it' : ''}</div>
              </div>
              <div className="text-slate-600 break-words">{whereOf(d)}</div>
              <div className="text-slate-600">{d.source === 'vendor' ? 'Vendor' : 'Staff'}</div>
              <div className="text-slate-600 tabular-nums">{fmtDate(d.created_at)}</div>
            </div>
          ))}
          {!docs.length && <div className="px-4 py-4 text-[12px] text-slate-500">No file name matches.</div>}
        </div>
      )}
    </div>
  );
}
